import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react';

// ════════════════════════════════════════════════════════════════════════════════
// NOVATION COMMAND CENTER v7.3 - SHOWINGS TRACKER EDITION
// Local Home Buyers USA | PropTechUSA.ai
// ════════════════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────────────────
// THEME SYSTEM
// ─────────────────────────────────────────────────────────────────────────────────
const DARK = {
  name: 'dark',
  bg: '#030712',
  bgAlt: '#0a0f1a',
  card: 'rgba(17, 24, 39, 0.8)',
  cardSolid: '#111827',
  input: '#1f2937',
  hover: '#374151',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.15)',
  text: '#f8fafc',
  textAlt: '#e2e8f0',
  muted: '#94a3b8',
  dim: '#64748b',
  accent: '#ffc107',
  accentAlt: '#ffca28',
  pink: '#ec4899',
  pinkAlt: '#f472b6',
  success: '#00e676',
  successAlt: '#4ade80',
  warn: '#f97316',
  warnAlt: '#fb923c',
  danger: '#ef4444',
  dangerAlt: '#f87171',
  info: '#00bcd4',
  infoAlt: '#22d3ee',
  purple: '#8b5cf6',
  purpleAlt: '#a78bfa',
  gold: '#ffd700',
  gradient1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  gradient5: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  glow: '0 0 40px rgba(255,193,7,0.3)',
  glowPink: '0 0 40px rgba(236,72,153,0.3)',
  glowBlue: '0 0 40px rgba(0,188,212,0.3)',
  glowGreen: '0 0 40px rgba(0,230,118,0.3)',
};

const LIGHT = {
  name: 'light',
  bg: '#f8fafc',
  bgAlt: '#f1f5f9',
  card: 'rgba(255, 255, 255, 0.9)',
  cardSolid: '#ffffff',
  input: '#f1f5f9',
  hover: '#e2e8f0',
  border: 'rgba(0,0,0,0.08)',
  borderLight: 'rgba(0,0,0,0.12)',
  text: '#0f172a',
  textAlt: '#1e293b',
  muted: '#64748b',
  dim: '#94a3b8',
  accent: '#d97706',
  accentAlt: '#f59e0b',
  pink: '#db2777',
  pinkAlt: '#ec4899',
  success: '#059669',
  successAlt: '#10b981',
  warn: '#ea580c',
  warnAlt: '#f97316',
  danger: '#dc2626',
  dangerAlt: '#ef4444',
  info: '#0891b2',
  infoAlt: '#06b6d4',
  purple: '#7c3aed',
  purpleAlt: '#8b5cf6',
  gold: '#b8860b',
  gradient1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  gradient5: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  glow: '0 0 40px rgba(217,119,6,0.2)',
  glowPink: '0 0 40px rgba(219,39,119,0.2)',
  glowBlue: '0 0 40px rgba(8,145,178,0.2)',
  glowGreen: '0 0 40px rgba(5,150,105,0.2)',
};

// ─────────────────────────────────────────────────────────────────────────────────
// USERS & ROLES
// ─────────────────────────────────────────────────────────────────────────────────
const DEFAULT_USERS = [
  { id: 'u1', username: 'justince777', password: 'Motivated777$', name: 'Justin', role: 'admin', avatar: 'JE' },
  { id: 'u2', username: 'sarah', password: 'Sarah2024!', name: 'Sarah', role: 'manager', avatar: 'SC' },
  { id: 'u3', username: 'mike', password: 'Mike2024!', name: 'Mike', role: 'acquisitions', avatar: 'MR' },
  { id: 'u4', username: 'Eric', password: 'signem123', name: 'Eric Esters', role: 'dispo', avatar: 'EE' },
  { id: 'u5', username: 'demo', password: 'demo123', name: 'Demo User', role: 'viewer', avatar: 'DU' },
];

const ROLES = {
  admin: { label: 'Admin', color: '#ec4899', canEdit: true, canDelete: true, canAdd: true, canExport: true, canManageUsers: true },
  manager: { label: 'Manager', color: '#8b5cf6', canEdit: true, canDelete: true, canAdd: true, canExport: true, canManageUsers: false },
  acquisitions: { label: 'Acquisitions', color: '#00bcd4', canEdit: true, canDelete: false, canAdd: true, canExport: false, canManageUsers: false },
  dispo: { label: 'Dispositions', color: '#00e676', canEdit: true, canDelete: false, canAdd: true, canExport: true, canManageUsers: false },
  viewer: { label: 'Viewer', color: '#64748b', canEdit: false, canDelete: false, canAdd: false, canExport: false, canManageUsers: false },
};

// ─────────────────────────────────────────────────────────────────────────────────
// STATES & STAGES
// ─────────────────────────────────────────────────────────────────────────────────
const US_STATES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',CT:'Connecticut',
  DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',
  KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',
  MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',
  OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',
  WI:'Wisconsin',WY:'Wyoming',DC:'Washington DC'
};

const STAGES = [
  { id: 'new_lead', label: 'New Lead', color: '#3b82f6', icon: '📥' },
  { id: 'contacted', label: 'Contacted', color: '#8b5cf6', icon: '📞' },
  { id: 'appointment', label: 'Appointment', color: '#f59e0b', icon: '📅' },
  { id: 'under_contract', label: 'Under Contract', color: '#ec4899', icon: '📝' },
  { id: 'listed', label: 'Listed/Dispo', color: '#00bcd4', icon: '🏠' },
  { id: 'pending', label: 'Pending', color: '#f97316', icon: '⏳' },
  { id: 'closed', label: 'Closed', color: '#00e676', icon: '✅' },
];

const SPECIAL_STAGES = [
  { id: 'on_hold', label: 'On Hold', color: '#64748b', icon: '⏸️' },
  { id: 'buyer_backed_out', label: 'Buyer Backed Out', color: '#f97316', icon: '🚫' },
  { id: 'dead_deal', label: 'Dead Deal', color: '#ef4444', icon: '💀' },
];

const ALL_STAGES = [...STAGES, ...SPECIAL_STAGES];

// ─────────────────────────────────────────────────────────────────────────────────
// SHOWING STATUSES - NEW IN v7.3
// ─────────────────────────────────────────────────────────────────────────────────
const SHOWING_STATUSES = [
  { id: 'scheduled', label: 'Scheduled', color: '#3b82f6', icon: '📅' },
  { id: 'completed', label: 'Completed', color: '#8b5cf6', icon: '✔️' },
  { id: 'needs_followup', label: 'Needs Follow-up', color: '#f97316', icon: '📞' },
  { id: 'feedback_received', label: 'Feedback Received', color: '#00bcd4', icon: '💬' },
  { id: 'interested', label: 'Interested', color: '#00e676', icon: '👍' },
  { id: 'not_interested', label: 'Not Interested', color: '#64748b', icon: '👎' },
  { id: 'offer_sent', label: 'Offer Sent', color: '#ec4899', icon: '📨' },
  { id: 'offer_received', label: 'Offer Received', color: '#ffc107', icon: '💰' },
  { id: 'cancelled', label: 'Cancelled', color: '#ef4444', icon: '❌' },
];

// ─────────────────────────────────────────────────────────────────────────────────
// RENTCAST API
// ─────────────────────────────────────────────────────────────────────────────────
const RENTCAST_API_KEY = '79531bfd42104ee29a4978808674ba34';
const RENTCAST_BASE = 'https://api.rentcast.io/v1';

const rentcastAPI = {
  async getPropertyValue(address, city, state, zip) {
    try {
      const params = new URLSearchParams({ address, city, state, zipCode: zip });
      const res = await fetch(`${RENTCAST_BASE}/avm/value?${params}`, {
        headers: { 'X-Api-Key': RENTCAST_API_KEY }
      });
      return res.ok ? await res.json() : null;
    } catch { return null; }
  },
  async getComps(address, city, state, zip, limit = 10) {
    try {
      const params = new URLSearchParams({ address, city, state, zipCode: zip, compCount: limit.toString() });
      const res = await fetch(`${RENTCAST_BASE}/avm/sale-comparables?${params}`, {
        headers: { 'X-Api-Key': RENTCAST_API_KEY }
      });
      return res.ok ? await res.json() : null;
    } catch { return null; }
  }
};

// ─────────────────────────────────────────────────────────────────────────────────
// FLAT FEE MLS SERVICES
// ─────────────────────────────────────────────────────────────────────────────────
const INITIAL_FLAT_FEE = [
  { id: 'ff1', name: 'Beycome', price: 99, states: ['FL','GA','AL','SC','NC','TN','TX'], url: 'https://beycome.com', features: ['6 months', '25 photos', 'MLS syndication'] },
  { id: 'ff2', name: 'Houzeo', price: 329, states: ['FL','TX','CA','NY','PA','OH','IL','GA','NC','AZ','MI','NJ'], url: 'https://houzeo.com', features: ['12 months', 'Unlimited photos', 'Nationwide'] },
  { id: 'ff3', name: 'List With Freedom', price: 149, states: ['FL','GA','NC','SC','VA'], url: 'https://listwithfreedom.com', features: ['6 months', '24 photos', 'Contract review'] },
  { id: 'ff4', name: 'Flat Fee Group', price: 299, states: ['MN','WI','IA','ND','SD'], url: 'https://flatfeegroup.com', features: ['12 months', '50 photos', 'Midwest specialist'] },
  { id: 'ff5', name: 'FSBO.com', price: 399, states: Object.keys(US_STATES), url: 'https://fsbo.com', features: ['12 months', 'All 50 states', 'Premium placement'] },
];

// ─────────────────────────────────────────────────────────────────────────────────
// INITIAL DATA
// ─────────────────────────────────────────────────────────────────────────────────
const INITIAL_DEALS = [
  { id: 'd1', address: '123 Main St', city: 'Phoenix', state: 'AZ', zip: '85001', stage: 'under_contract', askingPrice: 285000, offerPrice: 245000, arv: 340000, potentialSpread: 18000, assignmentFee: null, sellerName: 'John Smith', sellerPhone: '602-555-0101', sellerEmail: 'john@email.com', beds: 3, baths: 2, sqft: 1650, yearBuilt: 1998, propertyType: 'Single Family', notes: [{ id: 'n1', text: 'Motivated seller - divorce situation', date: '2024-12-15', user: 'Justin' }], createdAt: '2024-12-01', updatedAt: '2024-12-20', assignedTo: 'u1', buyerId: null, agentId: null, listedDate: null, dom: null, history: [{ stage: 'new_lead', date: '2024-12-01', user: 'Justin' }, { stage: 'contacted', date: '2024-12-05', user: 'Justin' }, { stage: 'under_contract', date: '2024-12-15', user: 'Justin' }] },
  { id: 'd2', address: '456 Oak Ave', city: 'Tampa', state: 'FL', zip: '33601', stage: 'listed', askingPrice: 195000, offerPrice: 165000, arv: 245000, potentialSpread: 15000, assignmentFee: null, sellerName: 'Mary Johnson', sellerPhone: '813-555-0202', sellerEmail: 'mary@email.com', beds: 4, baths: 2, sqft: 1890, yearBuilt: 2005, propertyType: 'Single Family', notes: [{ id: 'n2', text: 'Listed at $185k, getting good showing activity', date: '2024-12-18', user: 'Eric' }], createdAt: '2024-11-15', updatedAt: '2024-12-18', assignedTo: 'u4', buyerId: 'b1', agentId: 'a1', listedDate: '2024-12-10', listPrice: 185000, dom: null, history: [{ stage: 'new_lead', date: '2024-11-15', user: 'Eric' }, { stage: 'listed', date: '2024-12-10', user: 'Eric' }] },
  { id: 'd3', address: '789 Pine Rd', city: 'Atlanta', state: 'GA', zip: '30301', stage: 'closed', askingPrice: 320000, offerPrice: 275000, arv: 410000, potentialSpread: 25000, assignmentFee: 22000, sellerName: 'Bob Williams', sellerPhone: '404-555-0303', sellerEmail: 'bob@email.com', beds: 4, baths: 3, sqft: 2200, yearBuilt: 2010, propertyType: 'Single Family', notes: [{ id: 'n3', text: 'Closed in 21 days! Buyer was ABC Investments', date: '2024-12-10', user: 'Sarah' }], createdAt: '2024-10-20', updatedAt: '2024-12-10', assignedTo: 'u2', buyerId: 'b2', agentId: null, listedDate: '2024-11-20', closedDate: '2024-12-10', dom: 20, history: [{ stage: 'new_lead', date: '2024-10-20', user: 'Sarah' }, { stage: 'listed', date: '2024-11-20', user: 'Sarah' }, { stage: 'closed', date: '2024-12-10', user: 'Sarah' }] },
  { id: 'd4', address: '321 Elm St', city: 'Dallas', state: 'TX', zip: '75201', stage: 'listed', askingPrice: 225000, offerPrice: null, arv: 290000, potentialSpread: 15000, assignmentFee: null, sellerName: 'Lisa Davis', sellerPhone: '214-555-0404', sellerEmail: 'lisa@email.com', beds: 3, baths: 2, sqft: 1400, yearBuilt: 1985, propertyType: 'Single Family', notes: [], createdAt: '2024-12-20', updatedAt: '2024-12-28', assignedTo: 'u3', buyerId: null, agentId: null, listedDate: '2024-12-25', listPrice: 210000, dom: null, history: [{ stage: 'new_lead', date: '2024-12-20', user: 'Mike' }, { stage: 'listed', date: '2024-12-25', user: 'Mike' }] },
  { id: 'd5', address: '555 Cedar Ln', city: 'Denver', state: 'CO', zip: '80201', stage: 'pending', askingPrice: 375000, offerPrice: 335000, arv: 450000, potentialSpread: 20000, assignmentFee: null, sellerName: 'Tom Anderson', sellerPhone: '303-555-0505', sellerEmail: 'tom@email.com', beds: 4, baths: 3, sqft: 2400, yearBuilt: 2015, propertyType: 'Single Family', notes: [{ id: 'n4', text: 'Buyer Mountain View Capital - closing next week', date: '2024-12-25', user: 'Justin' }], createdAt: '2024-11-01', updatedAt: '2024-12-25', assignedTo: 'u1', buyerId: 'b3', agentId: 'a2', listedDate: '2024-12-01', listPrice: 395000, dom: 24, history: [{ stage: 'new_lead', date: '2024-11-01', user: 'Justin' }, { stage: 'listed', date: '2024-12-01', user: 'Justin' }, { stage: 'pending', date: '2024-12-20', user: 'Justin' }] },
];

const INITIAL_BUYERS = [
  { id: 'b1', name: 'ABC Investments', contact: 'Mike Chen', phone: '813-555-1001', email: 'mike@abcinvest.com', states: ['FL', 'GA'], criteria: 'SFH under $300k, 3+ bed', totalDeals: 5, lastDeal: '2024-12-10' },
  { id: 'b2', name: 'Peach State Properties', contact: 'Sarah Lee', phone: '404-555-1002', email: 'sarah@peachstate.com', states: ['GA', 'SC', 'NC'], criteria: 'Any condition, quick close', totalDeals: 8, lastDeal: '2024-12-10' },
  { id: 'b3', name: 'Mountain View Capital', contact: 'James Wilson', phone: '303-555-1003', email: 'james@mvcapital.com', states: ['CO', 'UT', 'AZ'], criteria: 'Fix & flip, $200k-$500k', totalDeals: 3, lastDeal: '2024-12-20' },
];

const INITIAL_AGENTS = [
  { id: 'a1', name: 'Jennifer Martinez', phone: '813-555-2001', email: 'jen@realty.com', brokerage: 'Keller Williams', states: ['FL'], commission: 2.5, activeListings: 2 },
  { id: 'a2', name: 'David Thompson', phone: '303-555-2002', email: 'david@homesmart.com', brokerage: 'HomeSmart', states: ['CO', 'AZ'], commission: 2.0, activeListings: 1 },
];

const INITIAL_CONTRACTORS = [
  { id: 'c1', name: 'Pro Renovations LLC', contact: 'Carlos Ruiz', phone: '602-555-3001', email: 'carlos@proreno.com', states: ['AZ', 'NV'], specialty: 'Full rehab', rating: 4.8 },
  { id: 'c2', name: 'Sunshine Repairs', contact: 'Amy Foster', phone: '813-555-3002', email: 'amy@sunshinerepairs.com', states: ['FL'], specialty: 'Cosmetic updates', rating: 4.5 },
];

// INITIAL SHOWINGS - NEW IN v7.3
const INITIAL_SHOWINGS = [
  { id: 's1', dealId: 'd2', date: '2024-12-28', time: '10:00 AM', buyerName: 'Robert Garcia', buyerPhone: '813-555-9001', buyerEmail: 'rgarcia@email.com', agentName: 'Jennifer Martinez', agentPhone: '813-555-2001', status: 'needs_followup', feedback: '', offerAmount: null, notes: 'First showing, buyer seemed interested in the layout', createdAt: '2024-12-26', updatedAt: '2024-12-28', createdBy: 'Eric' },
  { id: 's2', dealId: 'd2', date: '2024-12-29', time: '2:00 PM', buyerName: 'Amanda White', buyerPhone: '813-555-9002', buyerEmail: 'awhite@invest.com', agentName: 'Jennifer Martinez', agentPhone: '813-555-2001', status: 'feedback_received', feedback: 'Likes the property, considering making an offer. Wants to see comps.', offerAmount: null, notes: 'Investor, looking for rental properties', createdAt: '2024-12-27', updatedAt: '2024-12-30', createdBy: 'Eric' },
  { id: 's3', dealId: 'd4', date: '2025-01-03', time: '11:00 AM', buyerName: 'Carlos Rodriguez', buyerPhone: '214-555-9003', buyerEmail: 'carlos@realestate.com', agentName: '', agentPhone: '', status: 'scheduled', feedback: '', offerAmount: null, notes: 'Cash buyer, looking for fix and flip', createdAt: '2024-12-28', updatedAt: '2024-12-28', createdBy: 'Mike' },
  { id: 's4', dealId: 'd2', date: '2024-12-27', time: '4:00 PM', buyerName: 'David Kim', buyerPhone: '813-555-9004', buyerEmail: 'dkim@mail.com', agentName: 'Jennifer Martinez', agentPhone: '813-555-2001', status: 'offer_received', feedback: 'Very interested, submitted offer same day', offerAmount: 178000, notes: 'First time investor', createdAt: '2024-12-25', updatedAt: '2024-12-27', createdBy: 'Eric' },
];

// ─────────────────────────────────────────────────────────────────────────────────
// CONTEXTS
// ─────────────────────────────────────────────────────────────────────────────────
const ThemeCtx = createContext({ theme: DARK, isDark: true, toggle: () => {} });
const UserCtx = createContext(null);

function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('ncc-theme') !== 'light'; } catch { return true; }
  });
  const toggle = useCallback(() => {
    setIsDark(prev => {
      const next = !prev;
      try { localStorage.setItem('ncc-theme', next ? 'dark' : 'light'); } catch {}
      return next;
    });
  }, []);
  return <ThemeCtx.Provider value={{ theme: isDark ? DARK : LIGHT, isDark, toggle }}>{children}</ThemeCtx.Provider>;
}

const useTheme = () => useContext(ThemeCtx);

// ─────────────────────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────────────────────
const useStorage = (key, initial) => {
  const [data, setData] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
  }, [key, data]);
  return [data, setData];
};

const useClock = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  return time;
};

const useWeather = () => {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const geo = await fetch('https://ipapi.co/json/').then(r => r.json());
        const data = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${geo.latitude}&longitude=${geo.longitude}&current_weather=true`).then(r => r.json());
        setWeather({ temp: Math.round(data.current_weather.temperature * 9/5 + 32), city: geo.city, condition: data.current_weather.weathercode < 3 ? '☀️' : data.current_weather.weathercode < 50 ? '⛅' : '🌧️' });
      } catch { setWeather({ temp: 72, city: 'Unknown', condition: '☀️' }); }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, []);
  return weather;
};

// ─────────────────────────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────────────────────────
const fmt = (n) => n?.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) || '$0';
const fmtNum = (n) => n?.toLocaleString() || '0';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
const fmtTime = (d) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const daysBetween = (d1, d2) => Math.floor((new Date(d2) - new Date(d1)) / (1000 * 60 * 60 * 24));
const genId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const isThisWeek = (date) => {
  const now = new Date();
  const d = new Date(date);
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  return d >= startOfWeek;
};

const isThisMonth = (date) => {
  const now = new Date();
  const d = new Date(date);
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const isThisYear = (date) => {
  const now = new Date();
  const d = new Date(date);
  return d.getFullYear() === now.getFullYear();
};

const calculateDOM = (deal) => {
  if (deal.dom !== null && deal.dom !== undefined) return deal.dom;
  if (!deal.listedDate) return null;
  if (deal.closedDate) return Math.floor((new Date(deal.closedDate) - new Date(deal.listedDate)) / (1000 * 60 * 60 * 24));
  return Math.floor((new Date() - new Date(deal.listedDate)) / (1000 * 60 * 60 * 24));
};

// ─────────────────────────────────────────────────────────────────────────────────
// ANIMATED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────────
const GlowCard = ({ children, color, onClick, style = {} }) => {
  const { theme } = useTheme();
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: theme.card,
        backdropFilter: 'blur(20px)',
        borderRadius: 16,
        border: `1px solid ${hover ? color + '60' : theme.border}`,
        padding: 20,
        transition: 'all 0.3s ease',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? `0 8px 32px ${color}30` : 'none',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >{children}</div>
  );
};

const AnimatedNumber = ({ value, prefix = '', suffix = '', color }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const duration = 1000;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);
  return <span style={{ color, fontWeight: 800 }}>{prefix}{fmtNum(display)}{suffix}</span>;
};

const PulseOrb = ({ color, size = 12 }) => (
  <div style={{
    width: size,
    height: size,
    borderRadius: '50%',
    background: color,
    boxShadow: `0 0 ${size}px ${color}`,
    animation: 'pulse 2s ease-in-out infinite',
  }}>
    <style>{`@keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.1); } }`}</style>
  </div>
);

const SparkLine = ({ data, color, height = 40 }) => {
  const { theme } = useTheme();
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - ((v - min) / range) * 100}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,100 ${points} 100,100`} fill={`url(#grad-${color.replace('#', '')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const ProgressRing = ({ progress, size = 80, strokeWidth = 8, color }) => {
  const { theme } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={theme.input} strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
    </svg>
  );
};

const BarChart = ({ data, color }) => {
  const { theme } = useTheme();
  const max = Math.max(...data.map(d => d.value)) || 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 100 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: '100%',
            height: `${(d.value / max) * 80}px`,
            background: color,
            borderRadius: '4px 4px 0 0',
            transition: 'height 0.5s ease',
          }} />
          <span style={{ color: theme.muted, fontSize: 10 }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const DonutChart = ({ data, size = 120 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  let cumulativePercent = 0;
  
  const getCoordinatesForPercent = (percent) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  return (
    <svg viewBox="-1 -1 2 2" style={{ width: size, height: size, transform: 'rotate(-90deg)' }}>
      {data.map((slice, i) => {
        const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
        const slicePercent = slice.value / total;
        cumulativePercent += slicePercent;
        const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
        const largeArcFlag = slicePercent > 0.5 ? 1 : 0;
        const pathData = `M ${startX} ${startY} A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY} L 0 0`;
        return <path key={i} d={pathData} fill={slice.color} />;
      })}
      <circle cx="0" cy="0" r="0.6" fill="var(--bg, #030712)" />
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// LOGIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────────
const LoginScreen = ({ users, onLogin }) => {
  const { theme, toggle, isDark } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    // v7.2 FIX: Case-insensitive username matching
    const user = users.find(u => u.username.toLowerCase() === username.toLowerCase() && u.password === password);
    if (user) {
      onLogin(user);
    } else {
      setError('Invalid username or password');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <style>{`
        @keyframes shake { 0%, 100% { transform: translateX(0); } 20%, 60% { transform: translateX(-10px); } 40%, 80% { transform: translateX(10px); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>
      
      <div style={{
        width: '100%',
        maxWidth: 440,
        animation: shake ? 'shake 0.5s ease' : 'none',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 100,
            height: 100,
            background: theme.gradient1,
            borderRadius: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: 48,
            animation: 'float 3s ease-in-out infinite',
            boxShadow: theme.glow,
          }}>🏠</div>
          <h1 style={{ color: theme.text, fontSize: 32, fontWeight: 800, margin: 0 }}>Novation Command Center</h1>
          <p style={{ color: theme.muted, fontSize: 14, marginTop: 8 }}>Local Home Buyers USA • PropTechUSA.ai</p>
          <p style={{ color: theme.accent, fontSize: 12, marginTop: 4 }}>v7.3 Showings Tracker Edition</p>
        </div>

        <GlowCard color={theme.accent}>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: theme.muted, fontSize: 12, display: 'block', marginBottom: 8 }}>USERNAME</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: theme.input,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 10,
                  color: theme.text,
                  fontSize: 16,
                  boxSizing: 'border-box',
                }}
                placeholder="Enter username"
              />
            </div>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: theme.muted, fontSize: 12, display: 'block', marginBottom: 8 }}>PASSWORD</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: theme.input,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 10,
                  color: theme.text,
                  fontSize: 16,
                  boxSizing: 'border-box',
                }}
                placeholder="Enter password"
              />
            </div>

            {error && (
              <div style={{
                padding: 12,
                background: theme.danger + '20',
                border: `1px solid ${theme.danger}40`,
                borderRadius: 8,
                color: theme.danger,
                fontSize: 13,
                marginBottom: 20,
                textAlign: 'center',
              }}>{error}</div>
            )}

            <button type="submit" style={{
              width: '100%',
              padding: '16px',
              background: theme.gradient1,
              border: 'none',
              borderRadius: 12,
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}>Sign In</button>
          </form>

          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <button onClick={toggle} style={{
              background: 'none',
              border: 'none',
              color: theme.muted,
              fontSize: 13,
              cursor: 'pointer',
            }}>{isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}</button>
          </div>
        </GlowCard>

        <p style={{ textAlign: 'center', color: theme.dim, fontSize: 11, marginTop: 24 }}>
          Demo: username "demo" / password "demo123"
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// SUPER TICKER - Updated with Showings metrics
// ─────────────────────────────────────────────────────────────────────────────────
const SuperTicker = ({ deals, buyers, agents, showings }) => {
  const { theme } = useTheme();
  const weather = useWeather();
  const clock = useClock();
  
  const closedDeals = deals.filter(d => d.stage === 'closed');
  const ytdRevenue = closedDeals.filter(d => isThisYear(d.closedDate)).reduce((sum, d) => sum + (d.assignmentFee || 0), 0);
  const activeDeals = deals.filter(d => !['closed', 'dead_deal'].includes(d.stage)).length;
  const pipelineValue = deals.filter(d => !['closed', 'dead_deal'].includes(d.stage)).reduce((sum, d) => sum + (d.potentialSpread || 0), 0);
  const avgDeal = closedDeals.length ? ytdRevenue / closedDeals.length : 0;
  const uniqueStates = [...new Set(deals.map(d => d.state))].length;
  
  // Showings metrics - NEW IN v7.3
  const upcomingShowings = showings.filter(s => s.status === 'scheduled').length;
  const offersReceived = showings.filter(s => s.status === 'offer_received').length;

  const metrics = [
    { label: 'Revenue YTD', value: fmt(ytdRevenue), color: theme.success },
    { label: 'Active Deals', value: activeDeals, color: theme.info },
    { label: 'Pipeline Value', value: fmt(pipelineValue), color: theme.accent },
    { label: 'Closed Deals', value: closedDeals.length, color: theme.purple },
    { label: 'Avg Deal Size', value: fmt(avgDeal), color: theme.pink },
    { label: 'States Active', value: uniqueStates, color: theme.warn },
    { label: 'Buyer Network', value: buyers.length, color: theme.success },
    { label: 'Agent Partners', value: agents.length, color: theme.info },
    { label: 'Upcoming Showings', value: upcomingShowings, color: theme.accent },
    { label: 'Offers Received', value: offersReceived, color: theme.gold },
  ];

  return (
    <div style={{
      background: theme.bgAlt,
      borderBottom: `1px solid ${theme.border}`,
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: 24,
      overflowX: 'auto',
    }}>
      {weather && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16, borderRight: `1px solid ${theme.border}` }}>
          <span style={{ fontSize: 24 }}>{weather.condition}</span>
          <div>
            <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{weather.temp}°F</div>
            <div style={{ color: theme.muted, fontSize: 10 }}>{weather.city}</div>
          </div>
        </div>
      )}
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16, borderRight: `1px solid ${theme.border}` }}>
        <span style={{ fontSize: 20 }}>🕐</span>
        <div style={{ color: theme.text, fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>{fmtTime(clock)}</div>
      </div>

      {metrics.map((m, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <PulseOrb color={m.color} size={8} />
          <span style={{ color: theme.muted, fontSize: 11 }}>{m.label}</span>
          <span style={{ color: m.color, fontSize: 13, fontWeight: 700 }}>{m.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// SIDEBAR - Updated with Showings nav item
// ─────────────────────────────────────────────────────────────────────────────────
const Sidebar = ({ view, setView, user, onLogout, deals, showings }) => {
  const { theme, toggle, isDark } = useTheme();
  const role = ROLES[user.role];
  
  // Badge for follow-ups needed
  const needsFollowup = showings.filter(s => s.status === 'needs_followup').length;

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'deals', label: 'Deal Pipeline', icon: '💼' },
    { id: 'showings', label: 'Showings', icon: '📅', badge: needsFollowup > 0 ? needsFollowup : null },
    { id: 'tracker', label: 'Performance', icon: '📈' },
    { id: 'timeline', label: 'Timeline', icon: '⏱️' },
    { id: 'network', label: 'Network', icon: '🤝' },
    { id: 'markets', label: 'Markets', icon: '🗺️' },
    { id: 'flatfee', label: 'Flat Fee MLS', icon: '📋' },
    { id: 'financials', label: 'Financials', icon: '💰' },
    { id: 'onhold', label: 'On Hold', icon: '⏸️', count: deals.filter(d => ['on_hold', 'buyer_backed_out'].includes(d.stage)).length },
    { id: 'dead', label: 'Dead Deals', icon: '💀', count: deals.filter(d => d.stage === 'dead_deal').length },
    ...(role.canManageUsers ? [{ id: 'admin', label: 'Admin', icon: '⚙️' }] : []),
    { id: 'settings', label: 'Settings', icon: '🔧' },
  ];

  return (
    <div style={{
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      width: 260,
      background: theme.bgAlt,
      borderRight: `1px solid ${theme.border}`,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
    }}>
      <div style={{ padding: 20, borderBottom: `1px solid ${theme.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            background: theme.gradient1,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
          }}>🏠</div>
          <div>
            <div style={{ color: theme.text, fontSize: 15, fontWeight: 700 }}>Command Center</div>
            <div style={{ color: theme.muted, fontSize: 11 }}>v7.3 Showings</div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px 8px' }}>
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setView(item.id)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: view === item.id ? theme.accent + '20' : 'transparent',
              border: 'none',
              borderRadius: 10,
              color: view === item.id ? theme.accent : theme.text,
              fontSize: 14,
              fontWeight: view === item.id ? 600 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 4,
              transition: 'all 0.2s',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 18 }}>{item.icon}</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.count > 0 && (
              <span style={{
                background: theme.danger,
                color: '#fff',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 600,
              }}>{item.count}</span>
            )}
            {item.badge && (
              <span style={{
                background: theme.warn,
                color: '#fff',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 600,
              }}>{item.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: 16, borderTop: `1px solid ${theme.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            background: role.color,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
          }}>{user.avatar}</div>
          <div style={{ flex: 1 }}>
            <div style={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>{user.name}</div>
            <div style={{ color: role.color, fontSize: 11 }}>{role.label}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={toggle} style={{
            flex: 1,
            padding: '10px',
            background: theme.input,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            color: theme.text,
            fontSize: 12,
            cursor: 'pointer',
          }}>{isDark ? '☀️' : '🌙'}</button>
          <button onClick={onLogout} style={{
            flex: 1,
            padding: '10px',
            background: theme.danger + '20',
            border: `1px solid ${theme.danger}40`,
            borderRadius: 8,
            color: theme.danger,
            fontSize: 12,
            cursor: 'pointer',
          }}>Logout</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// MEGA DASHBOARD - Updated with Showings widgets
// ─────────────────────────────────────────────────────────────────────────────────
const MegaDashboard = ({ deals, buyers, agents, contractors, showings, user }) => {
  const { theme } = useTheme();
  
  const closedDeals = deals.filter(d => d.stage === 'closed');
  const activeDeals = deals.filter(d => !['closed', 'dead_deal'].includes(d.stage));
  const ytdRevenue = closedDeals.filter(d => isThisYear(d.closedDate)).reduce((sum, d) => sum + (d.assignmentFee || 0), 0);
  const mtdRevenue = closedDeals.filter(d => isThisMonth(d.closedDate)).reduce((sum, d) => sum + (d.assignmentFee || 0), 0);
  const pipelineValue = activeDeals.reduce((sum, d) => sum + (d.potentialSpread || 0), 0);
  
  // Showings data - NEW IN v7.3
  const upcomingShowings = showings.filter(s => s.status === 'scheduled');
  const needsFollowup = showings.filter(s => s.status === 'needs_followup');

  const recentActivity = [...deals]
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 5);

  const stageData = STAGES.map(stage => ({
    label: stage.icon,
    value: deals.filter(d => d.stage === stage.id).length,
  }));

  const sparkData = useMemo(() => {
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (5 - i));
      return d.toISOString().slice(0, 7);
    });
    return months.map(m => closedDeals.filter(d => d.closedDate?.slice(0, 7) === m).reduce((sum, d) => sum + (d.assignmentFee || 0), 0));
  }, [closedDeals]);

  const conversionRate = deals.length > 0 ? ((closedDeals.length / deals.length) * 100).toFixed(1) : 0;
  const avgDOM = closedDeals.reduce((sum, d) => sum + (calculateDOM(d) || 0), 0) / (closedDeals.length || 1);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>Welcome back, {user.name} 👋</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Here's what's happening with your deals</p>
      </div>

      {/* Main Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'YTD Revenue', value: ytdRevenue, prefix: '$', color: theme.success, icon: '💰', spark: sparkData },
          { label: 'MTD Revenue', value: mtdRevenue, prefix: '$', color: theme.info, icon: '📈' },
          { label: 'Pipeline Value', value: pipelineValue, prefix: '$', color: theme.accent, icon: '🎯' },
          { label: 'Active Deals', value: activeDeals.length, color: theme.purple, icon: '💼' },
        ].map((stat, i) => (
          <GlowCard key={i} color={stat.color}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ fontSize: 28 }}>{stat.icon}</div>
              {stat.spark && <SparkLine data={stat.spark} color={stat.color} height={30} />}
            </div>
            <div style={{ color: theme.text, fontSize: 28, fontWeight: 800 }}>
              {stat.prefix && stat.prefix}<AnimatedNumber value={stat.value} color={theme.text} />
            </div>
            <div style={{ color: theme.muted, fontSize: 12, marginTop: 4 }}>{stat.label}</div>
          </GlowCard>
        ))}
      </div>

      {/* Secondary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Deal Velocity', value: `${avgDOM.toFixed(0)} days`, subtext: 'avg days on market', color: theme.info, progress: Math.min(100 - avgDOM, 100) },
          { label: 'Conversion Rate', value: `${conversionRate}%`, subtext: 'deals to closed', color: theme.success, progress: parseFloat(conversionRate) },
          { label: 'Buyer Network', value: buyers.length, subtext: 'active buyers', color: theme.purple, progress: Math.min(buyers.length * 10, 100) },
          { label: 'Active Markets', value: [...new Set(deals.map(d => d.state))].length, subtext: 'states', color: theme.accent, progress: Math.min([...new Set(deals.map(d => d.state))].length * 10, 100) },
        ].map((stat, i) => (
          <GlowCard key={i} color={stat.color}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <ProgressRing progress={stat.progress} size={60} strokeWidth={6} color={stat.color} />
              <div>
                <div style={{ color: theme.text, fontSize: 24, fontWeight: 800 }}>{stat.value}</div>
                <div style={{ color: theme.muted, fontSize: 11 }}>{stat.label}</div>
                <div style={{ color: theme.dim, fontSize: 10 }}>{stat.subtext}</div>
              </div>
            </div>
          </GlowCard>
        ))}
      </div>

      {/* Grid Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Deal Pipeline by Stage */}
          <GlowCard color={theme.purple}>
            <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📊 Deal Pipeline by Stage</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {STAGES.map(stage => {
                const count = deals.filter(d => d.stage === stage.id).length;
                const pct = deals.length > 0 ? (count / deals.length) * 100 : 0;
                return (
                  <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 16, width: 24 }}>{stage.icon}</span>
                    <span style={{ color: theme.text, fontSize: 13, width: 120 }}>{stage.label}</span>
                    <div style={{ flex: 1, height: 20, background: theme.input, borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: stage.color,
                        borderRadius: 10,
                        transition: 'width 0.5s ease',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 8,
                      }}>
                        {pct > 10 && <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{count}</span>}
                      </div>
                    </div>
                    <span style={{ color: theme.muted, fontSize: 12, width: 30, textAlign: 'right' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </GlowCard>

          {/* Recent Activity */}
          <GlowCard color={theme.info}>
            <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>⚡ Recent Activity</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {recentActivity.map(deal => {
                const stage = ALL_STAGES.find(s => s.id === deal.stage);
                return (
                  <div key={deal.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 12,
                    background: theme.input,
                    borderRadius: 10,
                  }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      background: stage?.color + '30',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                    }}>{stage?.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: theme.text, fontSize: 13, fontWeight: 600 }}>{deal.address}</div>
                      <div style={{ color: theme.muted, fontSize: 11 }}>{deal.city}, {deal.state} • {stage?.label}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: theme.success, fontSize: 13, fontWeight: 600 }}>{fmt(deal.potentialSpread || deal.assignmentFee)}</div>
                      <div style={{ color: theme.dim, fontSize: 10 }}>{fmtDate(deal.updatedAt)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </GlowCard>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Stage Distribution Chart */}
          <GlowCard color={theme.accent}>
            <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📈 Stage Distribution</h3>
            <BarChart data={stageData} color={theme.accent} />
          </GlowCard>

          {/* Upcoming Showings - NEW IN v7.3 */}
          <GlowCard color={theme.info}>
            <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📅 Upcoming Showings</h3>
            {upcomingShowings.length === 0 ? (
              <div style={{ color: theme.muted, fontSize: 13, textAlign: 'center', padding: 20 }}>No showings scheduled</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {upcomingShowings.slice(0, 3).map(showing => {
                  const deal = deals.find(d => d.id === showing.dealId);
                  return (
                    <div key={showing.id} style={{ padding: 12, background: theme.input, borderRadius: 10 }}>
                      <div style={{ color: theme.text, fontSize: 13, fontWeight: 600 }}>{deal?.address || 'Unknown'}</div>
                      <div style={{ color: theme.muted, fontSize: 11, marginTop: 4 }}>
                        {showing.date} at {showing.time} • {showing.buyerName}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlowCard>

          {/* Needs Follow-up - NEW IN v7.3 */}
          <GlowCard color={theme.warn}>
            <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📞 Needs Follow-up</h3>
            {needsFollowup.length === 0 ? (
              <div style={{ color: theme.muted, fontSize: 13, textAlign: 'center', padding: 20 }}>All caught up! 🎉</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {needsFollowup.slice(0, 3).map(showing => {
                  const deal = deals.find(d => d.id === showing.dealId);
                  return (
                    <div key={showing.id} style={{ padding: 12, background: theme.input, borderRadius: 10, borderLeft: `3px solid ${theme.warn}` }}>
                      <div style={{ color: theme.text, fontSize: 13, fontWeight: 600 }}>{showing.buyerName}</div>
                      <div style={{ color: theme.muted, fontSize: 11, marginTop: 4 }}>
                        {deal?.address || 'Unknown'} • Shown {showing.date}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlowCard>

          {/* Conversion Donut */}
          <GlowCard color={theme.success}>
            <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>🎯 Deal Outcomes</h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
              <div style={{ '--bg': theme.cardSolid }}>
                <DonutChart data={[
                  { value: closedDeals.length, color: theme.success },
                  { value: deals.filter(d => d.stage === 'dead_deal').length, color: theme.danger },
                  { value: activeDeals.length, color: theme.info },
                ]} size={100} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Closed', value: closedDeals.length, color: theme.success },
                  { label: 'Active', value: activeDeals.length, color: theme.info },
                  { label: 'Dead', value: deals.filter(d => d.stage === 'dead_deal').length, color: theme.danger },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color }} />
                    <span style={{ color: theme.muted, fontSize: 11 }}>{item.label}</span>
                    <span style={{ color: theme.text, fontSize: 12, fontWeight: 600 }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </GlowCard>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// DEAL PIPELINE (KANBAN)
// ─────────────────────────────────────────────────────────────────────────────────
const DealPipeline = ({ deals, setDeals, buyers, agents, onEditDeal, user }) => {
  const { theme } = useTheme();
  const [draggedDeal, setDraggedDeal] = useState(null);
  const role = ROLES[user.role];

  const handleDragStart = (e, deal) => {
    if (!role.canEdit) return;
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (!draggedDeal || !role.canEdit) return;
    
    if (draggedDeal.stage !== stageId) {
      setDeals(prev => prev.map(d => d.id === draggedDeal.id ? {
        ...d,
        stage: stageId,
        updatedAt: new Date().toISOString(),
        listedDate: stageId === 'listed' && !d.listedDate ? new Date().toISOString() : d.listedDate,
        closedDate: stageId === 'closed' ? new Date().toISOString() : d.closedDate,
        history: [...(d.history || []), { stage: stageId, date: new Date().toISOString(), user: user.name }],
      } : d));
    }
    setDraggedDeal(null);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>💼 Deal Pipeline</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Drag and drop deals between stages</p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${STAGES.length}, minmax(200px, 1fr))`,
        gap: 16,
        overflowX: 'auto',
        paddingBottom: 20,
      }}>
        {STAGES.map(stage => {
          const stageDeals = deals.filter(d => d.stage === stage.id);
          const stageValue = stageDeals.reduce((sum, d) => sum + (d.potentialSpread || d.assignmentFee || 0), 0);
          
          return (
            <div
              key={stage.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
              style={{
                background: theme.bgAlt,
                borderRadius: 16,
                padding: 16,
                minHeight: 400,
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: `2px solid ${stage.color}`,
              }}>
                <span style={{ fontSize: 20 }}>{stage.icon}</span>
                <span style={{ color: theme.text, fontSize: 14, fontWeight: 700, flex: 1 }}>{stage.label}</span>
                <span style={{
                  background: stage.color + '30',
                  color: stage.color,
                  padding: '4px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                }}>{stageDeals.length}</span>
              </div>
              
              <div style={{ color: theme.success, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
                {fmt(stageValue)} potential
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {stageDeals.map(deal => {
                  const buyer = buyers.find(b => b.id === deal.buyerId);
                  const dom = calculateDOM(deal);
                  
                  return (
                    <div
                      key={deal.id}
                      draggable={role.canEdit}
                      onDragStart={(e) => handleDragStart(e, deal)}
                      onClick={() => onEditDeal(deal)}
                      style={{
                        background: theme.card,
                        borderRadius: 12,
                        padding: 14,
                        cursor: 'pointer',
                        border: `1px solid ${theme.border}`,
                        transition: 'all 0.2s',
                        opacity: draggedDeal?.id === deal.id ? 0.5 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ color: theme.text, fontSize: 13, fontWeight: 600, flex: 1 }}>{deal.address}</div>
                        {dom !== null && (
                          <span style={{
                            background: dom > 30 ? theme.danger + '30' : dom > 14 ? theme.warn + '30' : theme.success + '30',
                            color: dom > 30 ? theme.danger : dom > 14 ? theme.warn : theme.success,
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                          }}>{dom}d</span>
                        )}
                      </div>
                      <div style={{ color: theme.muted, fontSize: 11, marginBottom: 8 }}>{deal.city}, {deal.state}</div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: theme.success, fontSize: 13, fontWeight: 700 }}>
                          {fmt(deal.assignmentFee || deal.potentialSpread)}
                        </span>
                        {buyer && (
                          <span style={{
                            background: theme.info + '20',
                            color: theme.info,
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                          }}>{buyer.name.split(' ')[0]}</span>
                        )}
                      </div>

                      {deal.notes && deal.notes.length > 0 && (
                        <div style={{
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: `1px solid ${theme.border}`,
                          color: theme.dim,
                          fontSize: 11,
                          fontStyle: 'italic',
                        }}>
                          📝 {deal.notes[deal.notes.length - 1].text.slice(0, 60)}...
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// SHOWINGS TRACKER - NEW IN v7.3
// ─────────────────────────────────────────────────────────────────────────────────
const ShowingsTracker = ({ deals, showings, setShowings, user }) => {
  const { theme } = useTheme();
  const [showingModal, setShowingModal] = useState(null);
  const [filterDeal, setFilterDeal] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const role = ROLES[user.role];

  // Only show deals that are listed or pending
  const eligibleDeals = deals.filter(d => ['listed', 'pending'].includes(d.stage));

  const filteredShowings = showings.filter(s => {
    if (filterDeal && s.dealId !== filterDeal) return false;
    if (filterStatus && s.status !== filterStatus) return false;
    return true;
  });

  const stats = {
    total: showings.length,
    scheduled: showings.filter(s => s.status === 'scheduled').length,
    needsFollowup: showings.filter(s => s.status === 'needs_followup').length,
    interested: showings.filter(s => s.status === 'interested').length,
    offersReceived: showings.filter(s => s.status === 'offer_received').length,
  };

  const saveShowing = (showing) => {
    if (showings.find(s => s.id === showing.id)) {
      setShowings(prev => prev.map(s => s.id === showing.id ? showing : s));
    } else {
      setShowings(prev => [...prev, showing]);
    }
    setShowingModal(null);
  };

  const deleteShowing = (id) => {
    if (confirm('Delete this showing?')) {
      setShowings(prev => prev.filter(s => s.id !== id));
    }
  };

  const quickStatusUpdate = (showingId, newStatus) => {
    setShowings(prev => prev.map(s => s.id === showingId ? {
      ...s,
      status: newStatus,
      updatedAt: new Date().toISOString(),
    } : s));
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>📅 Showings Tracker</h1>
          <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Manage property showings and buyer feedback</p>
        </div>
        {role.canAdd && (
          <button onClick={() => setShowingModal({})} style={{
            padding: '12px 24px',
            background: theme.gradient1,
            border: 'none',
            borderRadius: 12,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}>+ Add Showing</button>
        )}
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Showings', value: stats.total, icon: '📋', color: theme.info },
          { label: 'Scheduled', value: stats.scheduled, icon: '📅', color: theme.accent },
          { label: 'Needs Follow-up', value: stats.needsFollowup, icon: '📞', color: theme.warn },
          { label: 'Interested', value: stats.interested, icon: '👍', color: theme.success },
          { label: 'Offers Received', value: stats.offersReceived, icon: '💰', color: theme.gold },
        ].map((stat, i) => (
          <GlowCard key={i} color={stat.color}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 28 }}>{stat.icon}</div>
              <div>
                <div style={{ color: theme.text, fontSize: 24, fontWeight: 800 }}>{stat.value}</div>
                <div style={{ color: theme.muted, fontSize: 11 }}>{stat.label}</div>
              </div>
            </div>
          </GlowCard>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <select
          value={filterDeal}
          onChange={(e) => setFilterDeal(e.target.value)}
          style={{
            padding: '12px 16px',
            background: theme.input,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
            color: theme.text,
            fontSize: 14,
            minWidth: 200,
          }}
        >
          <option value="">All Properties</option>
          {eligibleDeals.map(d => (
            <option key={d.id} value={d.id}>{d.address}, {d.city}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '12px 16px',
            background: theme.input,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
            color: theme.text,
            fontSize: 14,
            minWidth: 180,
          }}
        >
          <option value="">All Statuses</option>
          {SHOWING_STATUSES.map(s => (
            <option key={s.id} value={s.id}>{s.icon} {s.label}</option>
          ))}
        </select>
      </div>

      {/* Showings List */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
        {filteredShowings.map(showing => {
          const deal = deals.find(d => d.id === showing.dealId);
          const status = SHOWING_STATUSES.find(s => s.id === showing.status);
          
          return (
            <GlowCard key={showing.id} color={status?.color || theme.muted}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{deal?.address || 'Unknown Property'}</div>
                  <div style={{ color: theme.muted, fontSize: 12 }}>{deal?.city}, {deal?.state}</div>
                </div>
                <div style={{
                  background: status?.color + '20',
                  color: status?.color,
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                  <span>{status?.icon}</span>
                  <span>{status?.label}</span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ color: theme.dim, fontSize: 10, marginBottom: 4 }}>DATE & TIME</div>
                  <div style={{ color: theme.text, fontSize: 14 }}>{showing.date} at {showing.time}</div>
                </div>
                <div>
                  <div style={{ color: theme.dim, fontSize: 10, marginBottom: 4 }}>BUYER</div>
                  <div style={{ color: theme.text, fontSize: 14 }}>{showing.buyerName}</div>
                </div>
              </div>

              {showing.buyerPhone && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: theme.dim, fontSize: 10, marginBottom: 4 }}>BUYER CONTACT</div>
                  <div style={{ color: theme.info, fontSize: 13 }}>{showing.buyerPhone} • {showing.buyerEmail}</div>
                </div>
              )}

              {showing.agentName && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: theme.dim, fontSize: 10, marginBottom: 4 }}>SHOWING AGENT</div>
                  <div style={{ color: theme.text, fontSize: 13 }}>{showing.agentName}</div>
                </div>
              )}

              {showing.feedback && (
                <div style={{ marginBottom: 12, padding: 10, background: theme.input, borderRadius: 8 }}>
                  <div style={{ color: theme.dim, fontSize: 10, marginBottom: 4 }}>FEEDBACK</div>
                  <div style={{ color: theme.text, fontSize: 13 }}>{showing.feedback}</div>
                </div>
              )}

              {showing.offerAmount && (
                <div style={{ marginBottom: 12, padding: 10, background: theme.success + '15', borderRadius: 8, borderLeft: `3px solid ${theme.success}` }}>
                  <div style={{ color: theme.dim, fontSize: 10, marginBottom: 4 }}>OFFER AMOUNT</div>
                  <div style={{ color: theme.success, fontSize: 18, fontWeight: 700 }}>{fmt(showing.offerAmount)}</div>
                </div>
              )}

              {showing.notes && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: theme.dim, fontSize: 10, marginBottom: 4 }}>NOTES</div>
                  <div style={{ color: theme.muted, fontSize: 12, fontStyle: 'italic' }}>{showing.notes}</div>
                </div>
              )}

              {/* Quick Actions */}
              {role.canEdit && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
                  {showing.status === 'scheduled' && (
                    <>
                      <button onClick={() => quickStatusUpdate(showing.id, 'completed')} style={{ padding: '6px 10px', background: theme.success + '20', border: `1px solid ${theme.success}40`, borderRadius: 6, color: theme.success, fontSize: 11, cursor: 'pointer' }}>✓ Completed</button>
                      <button onClick={() => quickStatusUpdate(showing.id, 'cancelled')} style={{ padding: '6px 10px', background: theme.danger + '20', border: `1px solid ${theme.danger}40`, borderRadius: 6, color: theme.danger, fontSize: 11, cursor: 'pointer' }}>✕ Cancelled</button>
                    </>
                  )}
                  {showing.status === 'completed' && (
                    <>
                      <button onClick={() => quickStatusUpdate(showing.id, 'needs_followup')} style={{ padding: '6px 10px', background: theme.warn + '20', border: `1px solid ${theme.warn}40`, borderRadius: 6, color: theme.warn, fontSize: 11, cursor: 'pointer' }}>📞 Needs Follow-up</button>
                      <button onClick={() => quickStatusUpdate(showing.id, 'interested')} style={{ padding: '6px 10px', background: theme.success + '20', border: `1px solid ${theme.success}40`, borderRadius: 6, color: theme.success, fontSize: 11, cursor: 'pointer' }}>👍 Interested</button>
                      <button onClick={() => quickStatusUpdate(showing.id, 'not_interested')} style={{ padding: '6px 10px', background: theme.muted + '20', border: `1px solid ${theme.muted}40`, borderRadius: 6, color: theme.muted, fontSize: 11, cursor: 'pointer' }}>👎 Not Interested</button>
                    </>
                  )}
                  {showing.status === 'interested' && (
                    <button onClick={() => quickStatusUpdate(showing.id, 'offer_sent')} style={{ padding: '6px 10px', background: theme.pink + '20', border: `1px solid ${theme.pink}40`, borderRadius: 6, color: theme.pink, fontSize: 11, cursor: 'pointer' }}>📨 Offer Sent</button>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowingModal(showing)} style={{
                  flex: 1,
                  padding: '10px',
                  background: theme.input,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                  color: theme.text,
                  fontSize: 12,
                  cursor: 'pointer',
                }}>✏️ Edit</button>
                {role.canDelete && (
                  <button onClick={() => deleteShowing(showing.id)} style={{
                    padding: '10px 16px',
                    background: theme.danger + '20',
                    border: `1px solid ${theme.danger}40`,
                    borderRadius: 8,
                    color: theme.danger,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}>🗑️</button>
                )}
              </div>
            </GlowCard>
          );
        })}
      </div>

      {filteredShowings.length === 0 && (
        <GlowCard color={theme.muted}>
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>📅</div>
            <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>No showings found</div>
            <div style={{ color: theme.muted, fontSize: 14, marginTop: 8 }}>
              {showings.length === 0 ? 'Add your first showing to get started' : 'Try adjusting your filters'}
            </div>
          </div>
        </GlowCard>
      )}

      {/* Showing Modal */}
      {showingModal !== null && (
        <ShowingModal
          showing={showingModal.id ? showingModal : null}
          deals={eligibleDeals}
          onClose={() => setShowingModal(null)}
          onSave={saveShowing}
          user={user}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// SHOWING MODAL - NEW IN v7.3
// ─────────────────────────────────────────────────────────────────────────────────
const ShowingModal = ({ showing, deals, onClose, onSave, user }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState(showing || {
    dealId: deals[0]?.id || '',
    date: new Date().toISOString().split('T')[0],
    time: '10:00 AM',
    buyerName: '',
    buyerPhone: '',
    buyerEmail: '',
    agentName: '',
    agentPhone: '',
    status: 'scheduled',
    feedback: '',
    offerAmount: null,
    notes: '',
  });

  const handleSave = () => {
    if (!formData.dealId || !formData.buyerName) {
      alert('Please select a property and enter buyer name');
      return;
    }
    onSave({
      ...formData,
      id: formData.id || genId(),
      createdAt: formData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: formData.createdBy || user.name,
    });
  };

  const inputStyle = { width: '100%', padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, boxSizing: 'border-box', fontSize: 14 };
  const labelStyle = { color: theme.muted, fontSize: 11, display: 'block', marginBottom: 6, fontWeight: 600 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: theme.cardSolid, borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>{showing ? 'Edit Showing' : 'New Showing'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: theme.muted, fontSize: 28, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: 24 }}>
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <label style={labelStyle}>PROPERTY *</label>
              <select value={formData.dealId} onChange={(e) => setFormData({ ...formData, dealId: e.target.value })} style={inputStyle}>
                <option value="">Select a property...</option>
                {deals.map(d => (
                  <option key={d.id} value={d.id}>{d.address}, {d.city}, {d.state}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>DATE *</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>TIME</label>
                <input type="text" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} style={inputStyle} placeholder="10:00 AM" />
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 16, marginTop: 8 }}>
              <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>👤 Buyer Information</h4>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label style={labelStyle}>BUYER NAME *</label>
                  <input value={formData.buyerName} onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })} style={inputStyle} placeholder="John Smith" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>PHONE</label>
                    <input value={formData.buyerPhone} onChange={(e) => setFormData({ ...formData, buyerPhone: e.target.value })} style={inputStyle} placeholder="555-123-4567" />
                  </div>
                  <div>
                    <label style={labelStyle}>EMAIL</label>
                    <input value={formData.buyerEmail} onChange={(e) => setFormData({ ...formData, buyerEmail: e.target.value })} style={inputStyle} placeholder="buyer@email.com" />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 16, marginTop: 8 }}>
              <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>🏢 Showing Agent (optional)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>AGENT NAME</label>
                  <input value={formData.agentName} onChange={(e) => setFormData({ ...formData, agentName: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>AGENT PHONE</label>
                  <input value={formData.agentPhone} onChange={(e) => setFormData({ ...formData, agentPhone: e.target.value })} style={inputStyle} />
                </div>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 16, marginTop: 8 }}>
              <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>📊 Status & Feedback</h4>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label style={labelStyle}>STATUS</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} style={inputStyle}>
                    {SHOWING_STATUSES.map(s => (
                      <option key={s.id} value={s.id}>{s.icon} {s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>FEEDBACK</label>
                  <textarea value={formData.feedback} onChange={(e) => setFormData({ ...formData, feedback: e.target.value })} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Buyer feedback after showing..." />
                </div>
                <div>
                  <label style={labelStyle}>OFFER AMOUNT (if applicable)</label>
                  <input type="number" value={formData.offerAmount || ''} onChange={(e) => setFormData({ ...formData, offerAmount: e.target.value ? Number(e.target.value) : null })} style={inputStyle} placeholder="175000" />
                </div>
                <div>
                  <label style={labelStyle}>INTERNAL NOTES</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="Internal notes about this showing..." />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button onClick={onClose} style={{ padding: '12px 24px', background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 10, color: theme.text, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: '12px 32px', background: theme.gradient1, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>💾 Save Showing</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// PERFORMANCE TRACKER
// ─────────────────────────────────────────────────────────────────────────────────
const PerformanceTracker = ({ deals }) => {
  const { theme } = useTheme();
  const [period, setPeriod] = useState('monthly');
  
  const closedDeals = deals.filter(d => d.stage === 'closed');
  
  const getDealsForPeriod = (periodType) => {
    switch (periodType) {
      case 'weekly':
        return closedDeals.filter(d => isThisWeek(d.closedDate));
      case 'monthly':
        return closedDeals.filter(d => isThisMonth(d.closedDate));
      case 'yearly':
        return closedDeals.filter(d => isThisYear(d.closedDate));
      default:
        return closedDeals;
    }
  };

  const periodDeals = getDealsForPeriod(period);
  const periodRevenue = periodDeals.reduce((sum, d) => sum + (d.assignmentFee || 0), 0);
  const avgDealSize = periodDeals.length ? periodRevenue / periodDeals.length : 0;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div>
          <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>📈 Performance Tracker</h1>
          <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Track your deal performance over time</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['weekly', 'monthly', 'yearly'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '10px 20px',
                background: period === p ? theme.accent + '30' : theme.input,
                border: `1px solid ${period === p ? theme.accent : theme.border}`,
                borderRadius: 8,
                color: period === p ? theme.accent : theme.text,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >{p}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 32 }}>
        <GlowCard color={theme.success}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48 }}>💰</div>
            <div style={{ color: theme.text, fontSize: 36, fontWeight: 800, marginTop: 12 }}>{fmt(periodRevenue)}</div>
            <div style={{ color: theme.muted, fontSize: 14 }}>Revenue ({period})</div>
          </div>
        </GlowCard>
        <GlowCard color={theme.info}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48 }}>📊</div>
            <div style={{ color: theme.text, fontSize: 36, fontWeight: 800, marginTop: 12 }}>{periodDeals.length}</div>
            <div style={{ color: theme.muted, fontSize: 14 }}>Deals Closed ({period})</div>
          </div>
        </GlowCard>
        <GlowCard color={theme.purple}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48 }}>📈</div>
            <div style={{ color: theme.text, fontSize: 36, fontWeight: 800, marginTop: 12 }}>{fmt(avgDealSize)}</div>
            <div style={{ color: theme.muted, fontSize: 14 }}>Avg Deal Size ({period})</div>
          </div>
        </GlowCard>
      </div>

      <GlowCard color={theme.accent}>
        <h3 style={{ color: theme.text, fontSize: 18, fontWeight: 700, marginBottom: 20 }}>🏆 {period.charAt(0).toUpperCase() + period.slice(1)} Closed Deals</h3>
        {periodDeals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.muted }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div>No deals closed this {period.replace('ly', '')}</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {periodDeals.map(deal => (
              <div key={deal.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 16,
                background: theme.input,
                borderRadius: 12,
              }}>
                <div>
                  <div style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>{deal.address}</div>
                  <div style={{ color: theme.muted, fontSize: 12 }}>{deal.city}, {deal.state} • Closed {fmtDate(deal.closedDate)}</div>
                </div>
                <div style={{ color: theme.success, fontSize: 20, fontWeight: 800 }}>{fmt(deal.assignmentFee)}</div>
              </div>
            ))}
          </div>
        )}
      </GlowCard>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// ENHANCED TIMELINE
// ─────────────────────────────────────────────────────────────────────────────────
const EnhancedTimeline = ({ deals }) => {
  const { theme } = useTheme();
  
  const allEvents = deals.flatMap(deal => 
    (deal.history || []).map(h => ({
      ...h,
      deal,
      timestamp: new Date(h.date).getTime(),
    }))
  ).sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>⏱️ Timeline</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>{allEvents.length} events across all deals</p>
      </div>

      <div style={{ position: 'relative', paddingLeft: 40 }}>
        <div style={{
          position: 'absolute',
          left: 15,
          top: 0,
          bottom: 0,
          width: 2,
          background: theme.gradient1,
        }} />

        {allEvents.slice(0, 50).map((event, i) => {
          const stage = ALL_STAGES.find(s => s.id === event.stage);
          return (
            <div key={i} style={{ marginBottom: 24, position: 'relative' }}>
              <div style={{
                position: 'absolute',
                left: -25,
                width: 32,
                height: 32,
                background: stage?.color || theme.muted,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                boxShadow: `0 0 20px ${stage?.color || theme.muted}50`,
              }}>{stage?.icon || '📍'}</div>
              
              <GlowCard color={stage?.color || theme.muted} style={{ marginLeft: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ color: theme.text, fontSize: 15, fontWeight: 700 }}>{event.deal.address}</div>
                    <div style={{ color: theme.muted, fontSize: 12, marginTop: 2 }}>{event.deal.city}, {event.deal.state}</div>
                  </div>
                  <div style={{
                    background: stage?.color + '20',
                    color: stage?.color,
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                  }}>{stage?.label || event.stage}</div>
                </div>
                <div style={{ color: theme.dim, fontSize: 11, marginTop: 12 }}>
                  {event.user} • {fmtDate(event.date)}
                </div>
              </GlowCard>
            </div>
          );
        })}
      </div>

      {allEvents.length === 0 && (
        <GlowCard color={theme.muted}>
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>⏳</div>
            <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>No timeline events yet</div>
            <div style={{ color: theme.muted, fontSize: 14, marginTop: 8 }}>Events will appear here as deals progress</div>
          </div>
        </GlowCard>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// NETWORK VIEW
// ─────────────────────────────────────────────────────────────────────────────────
const NetworkView = ({ buyers, setBuyers, agents, setAgents, contractors, setContractors, user }) => {
  const { theme } = useTheme();
  const [tab, setTab] = useState('buyers');
  const [editingItem, setEditingItem] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const role = ROLES[user.role];

  const tabs = [
    { id: 'buyers', label: 'Buyers', icon: '💰', count: buyers.length },
    { id: 'agents', label: 'Agents', icon: '🏢', count: agents.length },
    { id: 'contractors', label: 'Contractors', icon: '🔧', count: contractors.length },
  ];

  const getCurrentData = () => {
    switch (tab) {
      case 'buyers': return { data: buyers, setData: setBuyers };
      case 'agents': return { data: agents, setData: setAgents };
      case 'contractors': return { data: contractors, setData: setContractors };
      default: return { data: [], setData: () => {} };
    }
  };

  const { data, setData } = getCurrentData();

  const saveItem = (item) => {
    if (data.find(d => d.id === item.id)) {
      setData(prev => prev.map(d => d.id === item.id ? item : d));
    } else {
      setData(prev => [...prev, { ...item, id: genId() }]);
    }
    setEditingItem(null);
    setShowAdd(false);
  };

  const deleteItem = (id) => {
    if (confirm('Delete this contact?')) {
      setData(prev => prev.filter(d => d.id !== id));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>🤝 Network</h1>
          <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Manage your buyers, agents, and contractors</p>
        </div>
        {role.canAdd && (
          <button onClick={() => setShowAdd(true)} style={{
            padding: '12px 24px',
            background: theme.gradient1,
            border: 'none',
            borderRadius: 12,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}>+ Add {tab.slice(0, -1)}</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '12px 20px',
              background: tab === t.id ? theme.accent + '20' : theme.input,
              border: `1px solid ${tab === t.id ? theme.accent : theme.border}`,
              borderRadius: 10,
              color: tab === t.id ? theme.accent : theme.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{t.icon}</span>
            <span>{t.label}</span>
            <span style={{
              background: tab === t.id ? theme.accent + '30' : theme.input,
              padding: '2px 8px',
              borderRadius: 6,
              fontSize: 12,
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 }}>
        {data.map(item => (
          <GlowCard key={item.id} color={theme.info}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
              <div style={{
                width: 50,
                height: 50,
                background: theme.gradient1,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
              }}>{item.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{item.name}</div>
                <div style={{ color: theme.muted, fontSize: 12 }}>{item.contact || item.brokerage || item.specialty}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
              {item.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: theme.dim }}>📞</span>
                  <span style={{ color: theme.text, fontSize: 13 }}>{item.phone}</span>
                </div>
              )}
              {item.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: theme.dim }}>✉️</span>
                  <span style={{ color: theme.info, fontSize: 13 }}>{item.email}</span>
                </div>
              )}
              {item.states && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: theme.dim }}>🗺️</span>
                  <span style={{ color: theme.muted, fontSize: 12 }}>{item.states.join(', ')}</span>
                </div>
              )}
              {item.criteria && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: theme.dim }}>🎯</span>
                  <span style={{ color: theme.muted, fontSize: 12 }}>{item.criteria}</span>
                </div>
              )}
            </div>

            {role.canEdit && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setEditingItem(item)} style={{
                  flex: 1,
                  padding: '10px',
                  background: theme.input,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                  color: theme.text,
                  fontSize: 12,
                  cursor: 'pointer',
                }}>✏️ Edit</button>
                {role.canDelete && (
                  <button onClick={() => deleteItem(item.id)} style={{
                    padding: '10px 16px',
                    background: theme.danger + '20',
                    border: `1px solid ${theme.danger}40`,
                    borderRadius: 8,
                    color: theme.danger,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}>🗑️</button>
                )}
              </div>
            )}
          </GlowCard>
        ))}
      </div>

      {data.length === 0 && (
        <GlowCard color={theme.muted}>
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>{tabs.find(t => t.id === tab)?.icon}</div>
            <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>No {tab} yet</div>
            <div style={{ color: theme.muted, fontSize: 14, marginTop: 8 }}>Add your first {tab.slice(0, -1)} to get started</div>
          </div>
        </GlowCard>
      )}

      {/* Edit/Add Modal would go here - simplified for brevity */}
      {(editingItem || showAdd) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setEditingItem(null); setShowAdd(false); }}>
          <div style={{ background: theme.cardSolid, borderRadius: 16, padding: 24, width: 400, maxWidth: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: theme.text, marginBottom: 16 }}>{editingItem ? 'Edit' : 'Add'} {tab.slice(0, -1)}</h3>
            <NetworkForm 
              item={editingItem || {}}
              type={tab}
              theme={theme}
              onSave={saveItem}
              onCancel={() => { setEditingItem(null); setShowAdd(false); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const NetworkForm = ({ item, type, theme, onSave, onCancel }) => {
  const [formData, setFormData] = useState(item);
  const inputStyle = { width: '100%', padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, boxSizing: 'border-box', fontSize: 14, marginBottom: 12 };

  return (
    <div>
      <input placeholder="Name" value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} style={inputStyle} />
      {type === 'buyers' && <input placeholder="Contact Person" value={formData.contact || ''} onChange={e => setFormData({ ...formData, contact: e.target.value })} style={inputStyle} />}
      <input placeholder="Phone" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} style={inputStyle} />
      <input placeholder="Email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} style={inputStyle} />
      {type === 'buyers' && <input placeholder="Criteria" value={formData.criteria || ''} onChange={e => setFormData({ ...formData, criteria: e.target.value })} style={inputStyle} />}
      {type === 'agents' && <input placeholder="Brokerage" value={formData.brokerage || ''} onChange={e => setFormData({ ...formData, brokerage: e.target.value })} style={inputStyle} />}
      {type === 'contractors' && <input placeholder="Specialty" value={formData.specialty || ''} onChange={e => setFormData({ ...formData, specialty: e.target.value })} style={inputStyle} />}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onSave(formData)} style={{ flex: 1, padding: 12, background: theme.gradient1, border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Save</button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// MARKETS VIEW
// ─────────────────────────────────────────────────────────────────────────────────
const MarketsView = ({ deals, buyers, agents }) => {
  const { theme } = useTheme();
  const [selectedState, setSelectedState] = useState(null);

  const stateMetrics = useMemo(() => {
    const states = {};
    deals.forEach(d => {
      if (!states[d.state]) {
        states[d.state] = { code: d.state, name: US_STATES[d.state], deals: 0, revenue: 0, pipeline: 0, active: 0, buyers: 0, stageBreakdown: [] };
      }
      states[d.state].deals++;
      if (d.stage === 'closed') states[d.state].revenue += d.assignmentFee || 0;
      if (!['closed', 'dead_deal'].includes(d.stage)) {
        states[d.state].pipeline += d.potentialSpread || 0;
        states[d.state].active++;
      }
    });
    buyers.forEach(b => {
      (b.states || []).forEach(s => {
        if (states[s]) states[s].buyers++;
      });
    });
    Object.keys(states).forEach(s => {
      states[s].stageBreakdown = STAGES.map(stage => ({
        ...stage,
        count: deals.filter(d => d.state === s && d.stage === stage.id).length,
      }));
    });
    return Object.values(states).sort((a, b) => b.deals - a.deals);
  }, [deals, buyers]);

  const topStates = stateMetrics.slice(0, 12);
  const closedDeals = deals.filter(d => d.stage === 'closed');

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>🗺️ Markets Overview</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Performance across {stateMetrics.length} active states</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <GlowCard color={theme.success}>
          <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📊 Revenue by State</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {topStates.filter(s => s.revenue > 0).slice(0, 5).map(state => (
              <div key={state.code} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: theme.muted, fontSize: 12, width: 30 }}>{state.code}</span>
                <div style={{ flex: 1, height: 20, background: theme.input, borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${(state.revenue / Math.max(...topStates.map(s => s.revenue))) * 100}%`,
                    background: theme.gradient4,
                    borderRadius: 10,
                  }} />
                </div>
                <span style={{ color: theme.success, fontSize: 12, fontWeight: 600, width: 80, textAlign: 'right' }}>{fmt(state.revenue)}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', color: theme.muted, fontSize: 12, marginTop: 16 }}>
            {closedDeals.length} of {deals.length} deals closed
          </div>
        </GlowCard>
        <GlowCard color={theme.accent}>
          <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📈 Stage Distribution</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {STAGES.map(stage => {
              const count = deals.filter(d => d.stage === stage.id).length;
              return (
                <div key={stage.id} style={{ textAlign: 'center', padding: 12, background: theme.input, borderRadius: 10 }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{stage.icon}</div>
                  <div style={{ color: theme.text, fontSize: 20, fontWeight: 700 }}>{count}</div>
                  <div style={{ color: theme.muted, fontSize: 10 }}>{stage.label}</div>
                </div>
              );
            })}
          </div>
        </GlowCard>
      </div>

      <h3 style={{ color: theme.text, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>🏆 Top Markets</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {topStates.map(state => (
          <GlowCard
            key={state.code}
            color={state.revenue > 0 ? theme.success : theme.info}
            onClick={() => setSelectedState(selectedState === state.code ? null : state.code)}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ color: theme.muted, fontSize: 12, fontWeight: 600 }}>{state.code}</div>
                <div style={{ color: theme.text, fontSize: 18, fontWeight: 700 }}>{state.name}</div>
              </div>
              <div style={{
                background: state.active > 0 ? theme.success + '20' : theme.input,
                padding: '4px 10px',
                borderRadius: 8,
              }}>
                <span style={{ color: state.active > 0 ? theme.success : theme.muted, fontSize: 12, fontWeight: 600 }}>
                  {state.active > 0 ? '🟢 Active' : '⚪ Inactive'}
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ color: theme.dim, fontSize: 11 }}>Deals</div>
                <div style={{ color: theme.text, fontSize: 18, fontWeight: 700 }}>{state.deals}</div>
              </div>
              <div>
                <div style={{ color: theme.dim, fontSize: 11 }}>Revenue</div>
                <div style={{ color: theme.success, fontSize: 18, fontWeight: 700 }}>{fmt(state.revenue)}</div>
              </div>
              <div>
                <div style={{ color: theme.dim, fontSize: 11 }}>Buyers</div>
                <div style={{ color: theme.info, fontSize: 18, fontWeight: 700 }}>{state.buyers}</div>
              </div>
              <div>
                <div style={{ color: theme.dim, fontSize: 11 }}>Pipeline</div>
                <div style={{ color: theme.accent, fontSize: 18, fontWeight: 700 }}>{fmt(state.pipeline)}</div>
              </div>
            </div>

            {selectedState === state.code && (
              <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 12, marginTop: 8 }}>
                <div style={{ color: theme.muted, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Stage Breakdown</div>
                {state.stageBreakdown.filter(s => s.count > 0).map(stage => (
                  <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>{stage.icon}</span>
                    <span style={{ color: theme.text, fontSize: 12, flex: 1 }}>{stage.label}</span>
                    <span style={{ color: stage.color, fontSize: 12, fontWeight: 600 }}>{stage.count}</span>
                  </div>
                ))}
              </div>
            )}
          </GlowCard>
        ))}
      </div>

      {stateMetrics.length === 0 && (
        <GlowCard color={theme.muted}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🗺️</div>
            <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>No market data yet</div>
            <div style={{ color: theme.muted, fontSize: 14, marginTop: 8 }}>Add deals to see market insights</div>
          </div>
        </GlowCard>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// FLAT FEE MLS VIEW
// ─────────────────────────────────────────────────────────────────────────────────
const FlatFeeView = ({ services }) => {
  const { theme } = useTheme();
  const [selectedState, setSelectedState] = useState(null);
  
  const statesWithServices = [...new Set(services.flatMap(s => s.states))].sort();
  
  const getServicesForState = (state) => {
    return services.filter(s => s.states.includes(state)).sort((a, b) => a.price - b.price);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>📋 Flat Fee MLS Services</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Compare flat fee listing services by state</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 32 }}>
        {services.map(service => (
          <GlowCard key={service.id} color={theme.info}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ color: theme.text, fontSize: 18, fontWeight: 700 }}>{service.name}</div>
                <a href={service.url} target="_blank" rel="noopener noreferrer" style={{ color: theme.info, fontSize: 12 }}>
                  Visit website →
                </a>
              </div>
              <div style={{
                background: theme.success + '20',
                color: theme.success,
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 18,
                fontWeight: 800,
              }}>${service.price}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              {service.features.map((f, i) => (
                <div key={i} style={{ color: theme.muted, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ color: theme.success }}>✓</span> {f}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {service.states.length > 8 ? (
                <span style={{ color: theme.accent, fontSize: 11, fontWeight: 600 }}>All 50 States</span>
              ) : (
                service.states.map(s => (
                  <span key={s} style={{
                    background: theme.input,
                    color: theme.muted,
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: 10,
                  }}>{s}</span>
                ))
              )}
            </div>
          </GlowCard>
        ))}
      </div>

      <GlowCard color={theme.purple}>
        <h3 style={{ color: theme.text, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>🔍 Find Best Service by State</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {statesWithServices.slice(0, 20).map(state => (
            <button
              key={state}
              onClick={() => setSelectedState(state === selectedState ? null : state)}
              style={{
                padding: '8px 14px',
                background: selectedState === state ? theme.accent + '30' : theme.input,
                border: `1px solid ${selectedState === state ? theme.accent : theme.border}`,
                borderRadius: 8,
                color: selectedState === state ? theme.accent : theme.text,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >{state}</button>
          ))}
        </div>
        
        {selectedState && (
          <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 16 }}>
            <h4 style={{ color: theme.text, fontSize: 16, marginBottom: 12 }}>
              Services available in {US_STATES[selectedState]}:
            </h4>
            {getServicesForState(selectedState).map((service, i) => (
              <div key={service.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                background: i === 0 ? theme.success + '15' : theme.input,
                borderRadius: 10,
                marginBottom: 8,
                border: i === 0 ? `1px solid ${theme.success}40` : 'none',
              }}>
                {i === 0 && <span style={{ color: theme.success, fontWeight: 700, fontSize: 12 }}>BEST VALUE</span>}
                <span style={{ color: theme.text, fontWeight: 600, flex: 1 }}>{service.name}</span>
                <span style={{ color: theme.success, fontWeight: 700 }}>${service.price}</span>
              </div>
            ))}
          </div>
        )}
      </GlowCard>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// FINANCIALS VIEW
// ─────────────────────────────────────────────────────────────────────────────────
const FinancialsView = ({ deals }) => {
  const { theme } = useTheme();
  
  const closedDeals = deals.filter(d => d.stage === 'closed');
  const totalRevenue = closedDeals.reduce((sum, d) => sum + (d.assignmentFee || 0), 0);
  const avgDealSize = closedDeals.length > 0 ? totalRevenue / closedDeals.length : 0;
  const pipelineValue = deals.filter(d => !['closed', 'dead_deal'].includes(d.stage)).reduce((sum, d) => sum + (d.assignmentFee || d.arv * 0.05 || 0), 0);
  
  const monthlyData = closedDeals.reduce((acc, d) => {
    const month = new Date(d.closedDate || d.updatedAt).toLocaleDateString('en-US', { month: 'short' });
    acc[month] = (acc[month] || 0) + (d.assignmentFee || 0);
    return acc;
  }, {});

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>💰 Financials</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Revenue tracking and financial metrics</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'Total Revenue', value: fmt(totalRevenue), color: theme.success, icon: '💰' },
          { label: 'Pipeline Value', value: fmt(pipelineValue), color: theme.info, icon: '📊' },
          { label: 'Avg Deal Size', value: fmt(avgDealSize), color: theme.purple, icon: '📈' },
          { label: 'Closed Deals', value: closedDeals.length, color: theme.accent, icon: '✅' },
        ].map((metric, i) => (
          <GlowCard key={i} color={metric.color}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{metric.icon}</div>
              <div style={{ color: theme.text, fontSize: 28, fontWeight: 800 }}>{metric.value}</div>
              <div style={{ color: theme.muted, fontSize: 12 }}>{metric.label}</div>
            </div>
          </GlowCard>
        ))}
      </div>

      <GlowCard color={theme.success}>
        <h3 style={{ color: theme.text, fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Monthly Revenue</h3>
        {Object.entries(monthlyData).length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {Object.entries(monthlyData).map(([month, amount]) => (
              <div key={month} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ color: theme.text, fontSize: 14, fontWeight: 600, width: 50 }}>{month}</div>
                <div style={{ flex: 1, height: 24, background: theme.input, borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${(amount / Math.max(...Object.values(monthlyData))) * 100}%`,
                    background: theme.gradient4,
                    borderRadius: 12,
                  }} />
                </div>
                <div style={{ color: theme.success, fontSize: 14, fontWeight: 700, width: 100, textAlign: 'right' }}>{fmt(amount)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: theme.muted, textAlign: 'center', padding: 40 }}>No closed deals yet.</div>
        )}
      </GlowCard>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// ON HOLD / DEAD DEALS VIEWS
// ─────────────────────────────────────────────────────────────────────────────────
const OnHoldView = ({ deals, setDeals, user }) => {
  const { theme } = useTheme();
  const onHoldDeals = deals.filter(d => d.stage === 'on_hold' || d.stage === 'buyer_backed_out');
  const role = ROLES[user.role];

  const restoreDeal = (dealId, newStage) => {
    if (!role.canEdit) return;
    setDeals(prev => prev.map(d => d.id === dealId ? {
      ...d,
      stage: newStage,
      updatedAt: new Date().toISOString(),
      history: [...(d.history || []), { stage: newStage, date: new Date().toISOString(), user: user.name }],
    } : d));
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>⏸️ On Hold / Buyer Issues</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>{onHoldDeals.length} deals need attention</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 }}>
        {onHoldDeals.map(deal => {
          const stage = SPECIAL_STAGES.find(s => s.id === deal.stage);
          return (
            <GlowCard key={deal.id} color={stage?.color}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 50,
                  height: 50,
                  background: stage?.color + '20',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                }}>{stage?.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{deal.address}</div>
                  <div style={{ color: theme.muted, fontSize: 13 }}>{deal.city}, {deal.state}</div>
                  <div style={{ color: stage?.color, fontSize: 12, marginTop: 4 }}>{stage?.label}</div>
                </div>
              </div>
              {role.canEdit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={() => restoreDeal(deal.id, 'new_lead')} style={{
                    flex: 1,
                    padding: '10px',
                    background: theme.info + '20',
                    border: `1px solid ${theme.info}40`,
                    borderRadius: 8,
                    color: theme.info,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>Restore to New Lead</button>
                  <button onClick={() => restoreDeal(deal.id, 'listed')} style={{
                    flex: 1,
                    padding: '10px',
                    background: theme.success + '20',
                    border: `1px solid ${theme.success}40`,
                    borderRadius: 8,
                    color: theme.success,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>Re-list</button>
                </div>
              )}
            </GlowCard>
          );
        })}
      </div>

      {onHoldDeals.length === 0 && (
        <GlowCard color={theme.muted}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
            <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>All clear!</div>
            <div style={{ color: theme.muted, fontSize: 14 }}>No deals on hold</div>
          </div>
        </GlowCard>
      )}
    </div>
  );
};

const DeadDealsView = ({ deals, setDeals, user }) => {
  const { theme } = useTheme();
  const deadDeals = deals.filter(d => d.stage === 'dead_deal');
  const role = ROLES[user.role];

  const restoreDeal = (dealId) => {
    if (!role.canEdit) return;
    setDeals(prev => prev.map(d => d.id === dealId ? {
      ...d,
      stage: 'new_lead',
      updatedAt: new Date().toISOString(),
      history: [...(d.history || []), { stage: 'new_lead', date: new Date().toISOString(), user: user.name }],
    } : d));
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>💀 Dead Deals</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>{deadDeals.length} deals archived</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 }}>
        {deadDeals.map(deal => (
          <GlowCard key={deal.id} color={theme.danger}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{
                width: 50,
                height: 50,
                background: theme.danger + '20',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
              }}>💀</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{deal.address}</div>
                <div style={{ color: theme.muted, fontSize: 13 }}>{deal.city}, {deal.state}</div>
                <div style={{ color: theme.danger, fontSize: 12, marginTop: 4 }}>Dead Deal</div>
              </div>
            </div>
            {role.canEdit && (
              <button onClick={() => restoreDeal(deal.id)} style={{
                width: '100%',
                marginTop: 16,
                padding: '10px',
                background: theme.success + '20',
                border: `1px solid ${theme.success}40`,
                borderRadius: 8,
                color: theme.success,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}>♻️ Revive Deal</button>
            )}
          </GlowCard>
        ))}
      </div>

      {deadDeals.length === 0 && (
        <GlowCard color={theme.muted}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>No dead deals!</div>
            <div style={{ color: theme.muted, fontSize: 14 }}>All your deals are still alive</div>
          </div>
        </GlowCard>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// ADMIN VIEW - v7.2 FIX: Functional state updates
// ─────────────────────────────────────────────────────────────────────────────────
const AdminView = ({ users, setUsers, currentUser }) => {
  const { theme } = useTheme();
  const [editingUser, setEditingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newUserForm, setNewUserForm] = useState({ username: '', password: '', name: '', role: 'viewer' });
  const [showAddUser, setShowAddUser] = useState(false);

  const updateUserRole = (userId, newRole) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const updateUserPassword = (userId) => {
    if (!newPassword.trim()) return;
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, password: newPassword } : u));
    setEditingUser(null);
    setNewPassword('');
  };

  const deleteUser = (userId) => {
    if (userId === currentUser.id) return;
    setUsers(prev => prev.filter(u => u.id !== userId));
  };

  const addUser = () => {
    if (!newUserForm.username || !newUserForm.password || !newUserForm.name) return;
    setUsers(prev => [...prev, {
      id: genId(),
      ...newUserForm,
      avatar: newUserForm.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
    }]);
    setNewUserForm({ username: '', password: '', name: '', role: 'viewer' });
    setShowAddUser(false);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>⚙️ Admin Panel</h1>
          <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Manage users and permissions</p>
        </div>
        <button onClick={() => setShowAddUser(true)} style={{
          padding: '12px 20px',
          background: theme.gradient1,
          border: 'none',
          borderRadius: 10,
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}>+ Add User</button>
      </div>

      {showAddUser && (
        <GlowCard color={theme.accent} style={{ marginBottom: 24 }}>
          <h3 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Add New User</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            <input placeholder="Username" value={newUserForm.username} onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })} style={{ padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text }} />
            <input placeholder="Password" value={newUserForm.password} onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })} style={{ padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text }} />
            <input placeholder="Full Name" value={newUserForm.name} onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })} style={{ padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text }} />
            <select value={newUserForm.role} onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })} style={{ padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text }}>
              {Object.entries(ROLES).map(([key, r]) => <option key={key} value={key}>{r.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={addUser} style={{ padding: '10px 20px', background: theme.success, border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Create User</button>
            <button onClick={() => setShowAddUser(false)} style={{ padding: '10px 20px', background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, cursor: 'pointer' }}>Cancel</button>
          </div>
        </GlowCard>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 }}>
        {users.map(user => {
          const role = ROLES[user.role];
          const isCurrentUser = user.id === currentUser.id;
          const isEditing = editingUser === user.id;
          
          return (
            <GlowCard key={user.id} color={role.color}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                <div style={{
                  width: 50,
                  height: 50,
                  background: role.color,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 16,
                }}>{user.avatar}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{user.name}</div>
                  <div style={{ color: theme.muted, fontSize: 12 }}>@{user.username}</div>
                </div>
                {isCurrentUser && <span style={{ color: theme.accent, fontSize: 11, fontWeight: 600 }}>YOU</span>}
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ color: theme.muted, fontSize: 11, display: 'block', marginBottom: 4 }}>ROLE</label>
                <select value={user.role} onChange={(e) => updateUserRole(user.id, e.target.value)} disabled={isCurrentUser} style={{ width: '100%', padding: 10, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, opacity: isCurrentUser ? 0.5 : 1 }}>
                  {Object.entries(ROLES).map(([key, r]) => <option key={key} value={key}>{r.label}</option>)}
                </select>
              </div>

              {isEditing ? (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ color: theme.muted, fontSize: 11, display: 'block', marginBottom: 4 }}>NEW PASSWORD</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" style={{ flex: 1, padding: 10, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text }} />
                    <button onClick={() => updateUserPassword(user.id)} style={{ padding: '10px 16px', background: theme.success, border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Save</button>
                    <button onClick={() => { setEditingUser(null); setNewPassword(''); }} style={{ padding: '10px 16px', background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setEditingUser(user.id)} style={{ width: '100%', padding: '10px', background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 12, cursor: 'pointer', marginBottom: 12 }}>🔐 Change Password</button>
              )}

              {!isCurrentUser && (
                <button onClick={() => deleteUser(user.id)} style={{ width: '100%', padding: '10px', background: theme.danger + '20', border: `1px solid ${theme.danger}40`, borderRadius: 8, color: theme.danger, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🗑️ Delete User</button>
              )}
            </GlowCard>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// SETTINGS VIEW
// ─────────────────────────────────────────────────────────────────────────────────
const SettingsView = ({ deals, setDeals, buyers, setBuyers, agents, setAgents, contractors, setContractors, users, setUsers, showings, setShowings, user }) => {
  const { theme } = useTheme();
  const role = ROLES[user.role];
  const fileInputRef = useRef(null);
  const [importStatus, setImportStatus] = useState(null);

  const exportData = () => {
    const data = { deals, buyers, agents, contractors, users, showings, exportedAt: new Date().toISOString(), version: '7.3' };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ncc-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        if (typeof text !== 'string') throw new Error('Invalid file');
        
        const data = JSON.parse(text);
        if (!data || typeof data !== 'object') throw new Error('Invalid JSON');
        
        if (Array.isArray(data.deals)) setDeals(data.deals);
        if (Array.isArray(data.buyers)) setBuyers(data.buyers);
        if (Array.isArray(data.agents)) setAgents(data.agents);
        if (Array.isArray(data.contractors)) setContractors(data.contractors);
        if (Array.isArray(data.users)) setUsers(data.users);
        if (Array.isArray(data.showings)) setShowings(data.showings);
        
        setImportStatus({ success: true, message: `Imported: ${data.deals?.length || 0} deals, ${data.buyers?.length || 0} buyers, ${data.showings?.length || 0} showings` });
        setTimeout(() => setImportStatus(null), 5000);
      } catch (err) {
        setImportStatus({ success: false, message: `Error: ${err.message}` });
        setTimeout(() => setImportStatus(null), 5000);
      }
    };
    reader.onerror = () => {
      setImportStatus({ success: false, message: 'Error reading file' });
      setTimeout(() => setImportStatus(null), 5000);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearAllData = () => {
    if (confirm('Are you sure you want to clear ALL data? This cannot be undone.')) {
      setDeals(INITIAL_DEALS);
      setBuyers(INITIAL_BUYERS);
      setAgents(INITIAL_AGENTS);
      setContractors(INITIAL_CONTRACTORS);
      setUsers(DEFAULT_USERS);
      setShowings(INITIAL_SHOWINGS);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: theme.text, fontSize: 28, fontWeight: 800, margin: 0 }}>🔧 Settings</h1>
        <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>Manage your data and preferences</p>
      </div>

      {importStatus && (
        <div style={{ padding: 16, marginBottom: 24, borderRadius: 12, background: importStatus.success ? theme.success + '20' : theme.danger + '20', border: `1px solid ${importStatus.success ? theme.success : theme.danger}40`, color: importStatus.success ? theme.success : theme.danger, fontWeight: 600 }}>
          {importStatus.success ? '✅' : '❌'} {importStatus.message}
        </div>
      )}

      <GlowCard color={theme.info} style={{ marginBottom: 24 }}>
        <h3 style={{ color: theme.text, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📊 Data Overview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16 }}>
          {[
            { label: 'Deals', value: deals.length, icon: '💼' },
            { label: 'Showings', value: showings.length, icon: '📅' },
            { label: 'Buyers', value: buyers.length, icon: '💰' },
            { label: 'Agents', value: agents.length, icon: '🏢' },
            { label: 'Contractors', value: contractors.length, icon: '🔧' },
            { label: 'Users', value: users.length, icon: '👥' },
          ].map((stat, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24 }}>{stat.icon}</div>
              <div style={{ color: theme.text, fontSize: 24, fontWeight: 800 }}>{stat.value}</div>
              <div style={{ color: theme.muted, fontSize: 12 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </GlowCard>

      {role.canExport && (
        <GlowCard color={theme.success} style={{ marginBottom: 24 }}>
          <h3 style={{ color: theme.text, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>💾 Backup & Restore</h3>
          <div style={{ display: 'flex', gap: 16 }}>
            <button onClick={exportData} style={{
              flex: 1, padding: '16px', background: theme.success + '20', border: `1px solid ${theme.success}40`,
              borderRadius: 12, color: theme.success, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}><span style={{ fontSize: 20 }}>📤</span>Export All Data</button>
            <button onClick={() => fileInputRef.current?.click()} style={{
              flex: 1, padding: '16px', background: theme.info + '20', border: `1px solid ${theme.info}40`,
              borderRadius: 12, color: theme.info, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}><span style={{ fontSize: 20 }}>📥</span>Import Data</button>
            <input ref={fileInputRef} type="file" accept=".json" onChange={importData} style={{ display: 'none' }} />
          </div>
        </GlowCard>
      )}

      {role.canDelete && (
        <GlowCard color={theme.danger}>
          <h3 style={{ color: theme.danger, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>⚠️ Danger Zone</h3>
          <p style={{ color: theme.muted, fontSize: 13, marginBottom: 16 }}>Reset all data to defaults. This action cannot be undone.</p>
          <button onClick={clearAllData} style={{ padding: '12px 24px', background: theme.danger, border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>🗑️ Reset All Data</button>
        </GlowCard>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// DEAL MODAL
// ─────────────────────────────────────────────────────────────────────────────────
const DealModal = ({ deal, onClose, onSave, onDelete, buyers, agents, user }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState(deal || {
    address: '', city: '', state: 'FL', zip: '', stage: 'new_lead',
    askingPrice: '', offerPrice: '', arv: '', potentialSpread: '', assignmentFee: '',
    sellerName: '', sellerPhone: '', sellerEmail: '',
    beds: '', baths: '', sqft: '', yearBuilt: '', propertyType: 'Single Family',
    notes: [], buyerId: null, agentId: null, history: [], rentcastData: null, comps: [], dom: null,
  });
  const [newNote, setNewNote] = useState('');
  const [tab, setTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const role = ROLES[user.role];

  const handleSave = () => {
    onSave({
      ...formData,
      id: formData.id || genId(),
      createdAt: formData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      assignedTo: formData.assignedTo || user.id,
      history: formData.history?.length ? formData.history : [{ stage: formData.stage, date: new Date().toISOString(), user: user.name }],
    });
    onClose();
  };

  const addNote = () => {
    if (!newNote.trim()) return;
    setFormData({
      ...formData,
      notes: [...(formData.notes || []), { id: genId(), text: newNote, date: new Date().toISOString(), user: user.name }],
    });
    setNewNote('');
  };

  const updateNote = (noteId, newText) => {
    setFormData({
      ...formData,
      notes: formData.notes.map(n => n.id === noteId ? { ...n, text: newText, editedAt: new Date().toISOString() } : n),
    });
    setEditingNote(null);
  };

  const deleteNote = (noteId) => {
    if (!role.canEdit) return;
    setFormData({ ...formData, notes: formData.notes.filter(n => n.id !== noteId) });
  };

  const fetchRentCast = async () => {
    if (!formData.address || !formData.city || !formData.state || !formData.zip) return;
    setLoading(true);
    try {
      const [valueData, compsData] = await Promise.all([
        rentcastAPI.getPropertyValue(formData.address, formData.city, formData.state, formData.zip),
        rentcastAPI.getComps(formData.address, formData.city, formData.state, formData.zip, 5),
      ]);
      setFormData({
        ...formData,
        rentcastData: valueData,
        comps: compsData?.comparables || [],
        arv: valueData?.price || formData.arv,
        beds: valueData?.bedrooms || formData.beds,
        baths: valueData?.bathrooms || formData.baths,
        sqft: valueData?.squareFootage || formData.sqft,
        yearBuilt: valueData?.yearBuilt || formData.yearBuilt,
      });
    } catch (err) { console.error('RentCast error:', err); }
    setLoading(false);
  };

  const inputStyle = { width: '100%', padding: 12, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, boxSizing: 'border-box', fontSize: 14 };
  const labelStyle = { color: theme.muted, fontSize: 11, display: 'block', marginBottom: 6, fontWeight: 600 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: theme.cardSolid, borderRadius: 20, width: '100%', maxWidth: 900, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>{deal ? 'Edit Deal' : 'New Deal'}</h2>
            {deal && <p style={{ color: theme.muted, fontSize: 12, margin: '4px 0 0' }}>{deal.address}, {deal.city}, {deal.state}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: theme.muted, fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}` }}>
          {['details', 'financials', 'notes', 'rentcast', 'history'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, padding: '14px', background: tab === t ? theme.accent + '15' : 'transparent', border: 'none',
              borderBottom: tab === t ? `2px solid ${theme.accent}` : '2px solid transparent',
              color: tab === t ? theme.accent : theme.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
            }}>{t === 'rentcast' ? '🏠 RentCast' : t}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {tab === 'details' && (
            <div style={{ display: 'grid', gap: 20 }}>
              <div>
                <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📍 Property Address</h4>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div><label style={labelStyle}>STREET ADDRESS</label><input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} style={inputStyle} placeholder="123 Main St" /></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                    <div><label style={labelStyle}>CITY</label><input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} style={inputStyle} /></div>
                    <div><label style={labelStyle}>STATE</label><select value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} style={inputStyle}>{Object.entries(US_STATES).map(([code]) => <option key={code} value={code}>{code}</option>)}</select></div>
                    <div><label style={labelStyle}>ZIP</label><input value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} style={inputStyle} /></div>
                  </div>
                </div>
              </div>
              <div>
                <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>🏠 Property Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                  <div><label style={labelStyle}>BEDS</label><input type="number" value={formData.beds || ''} onChange={(e) => setFormData({ ...formData, beds: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>BATHS</label><input type="number" step="0.5" value={formData.baths || ''} onChange={(e) => setFormData({ ...formData, baths: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>SQFT</label><input type="number" value={formData.sqft || ''} onChange={(e) => setFormData({ ...formData, sqft: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>YEAR BUILT</label><input type="number" value={formData.yearBuilt || ''} onChange={(e) => setFormData({ ...formData, yearBuilt: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>TYPE</label><select value={formData.propertyType || 'Single Family'} onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })} style={inputStyle}><option>Single Family</option><option>Multi Family</option><option>Condo</option><option>Townhouse</option><option>Land</option></select></div>
                </div>
              </div>
              <div>
                <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>👤 Seller Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  <div><label style={labelStyle}>SELLER NAME</label><input value={formData.sellerName} onChange={(e) => setFormData({ ...formData, sellerName: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>PHONE</label><input value={formData.sellerPhone} onChange={(e) => setFormData({ ...formData, sellerPhone: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>EMAIL</label><input value={formData.sellerEmail} onChange={(e) => setFormData({ ...formData, sellerEmail: e.target.value })} style={inputStyle} /></div>
                </div>
              </div>
              <div><label style={labelStyle}>DEAL STAGE</label><select value={formData.stage} onChange={(e) => setFormData({ ...formData, stage: e.target.value })} style={inputStyle}>{ALL_STAGES.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}</select></div>
            </div>
          )}

          {tab === 'financials' && (
            <div style={{ display: 'grid', gap: 20 }}>
              <div>
                <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>💰 Deal Numbers</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                  {[
                    { key: 'askingPrice', label: 'Asking Price', icon: '🏷️', color: theme.text },
                    { key: 'offerPrice', label: 'Our Offer', icon: '📝', color: theme.info },
                    { key: 'arv', label: 'ARV', icon: '📈', color: theme.purple },
                    { key: 'potentialSpread', label: 'Potential Spread', icon: '🎯', color: theme.accent },
                    { key: 'assignmentFee', label: 'Closed/Funded Amount', icon: '💵', color: theme.success },
                  ].map(field => (
                    <div key={field.key} style={{ background: theme.input, borderRadius: 12, padding: 16, borderLeft: `4px solid ${field.color}` }}>
                      <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><span>{field.icon}</span> {field.label.toUpperCase()}</label>
                      <input type="number" value={formData[field.key] || ''} onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value ? Number(e.target.value) : null })} style={{ ...inputStyle, fontSize: 20, fontWeight: 700, background: 'transparent', border: 'none', padding: 0 }} placeholder="$0" />
                      {formData[field.key] && <div style={{ color: field.color, fontSize: 12, marginTop: 4 }}>{fmt(formData[field.key])}</div>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 style={{ color: theme.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>🤝 Assignments</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                  <div><label style={labelStyle}>ASSIGNED BUYER</label><select value={formData.buyerId || ''} onChange={(e) => setFormData({ ...formData, buyerId: e.target.value || null })} style={inputStyle}><option value="">Select buyer...</option>{buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
                  <div><label style={labelStyle}>LISTING AGENT</label><select value={formData.agentId || ''} onChange={(e) => setFormData({ ...formData, agentId: e.target.value || null })} style={inputStyle}><option value="">Select agent...</option>{(agents || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
                </div>
              </div>
            </div>
          )}

          {tab === 'notes' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." style={{ flex: 1, padding: 14, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 10, color: theme.text, fontSize: 14 }} onKeyPress={(e) => e.key === 'Enter' && addNote()} />
                <button onClick={addNote} style={{ padding: '14px 24px', background: theme.gradient1, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Add Note</button>
              </div>
              {(formData.notes || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: theme.muted }}><div style={{ fontSize: 40, marginBottom: 12 }}>📝</div><div>No notes yet.</div></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(formData.notes || []).map(note => (
                    <div key={note.id} style={{ padding: 16, background: theme.input, borderRadius: 12, border: `1px solid ${theme.border}` }}>
                      {editingNote === note.id ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input defaultValue={note.text} id={`note-edit-${note.id}`} style={{ flex: 1, padding: 10, background: theme.cardSolid, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text }} />
                          <button onClick={() => updateNote(note.id, document.getElementById(`note-edit-${note.id}`).value)} style={{ padding: '10px 16px', background: theme.success, border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Save</button>
                          <button onClick={() => setEditingNote(null)} style={{ padding: '10px 16px', background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, cursor: 'pointer' }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: theme.text, fontSize: 14, lineHeight: 1.5 }}>{note.text}</div>
                            <div style={{ color: theme.muted, fontSize: 11, marginTop: 8 }}>{note.user} • {fmtDate(note.date)}</div>
                          </div>
                          {role.canEdit && (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => setEditingNote(note.id)} style={{ background: 'none', border: 'none', color: theme.info, cursor: 'pointer', fontSize: 14 }}>✏️</button>
                              <button onClick={() => deleteNote(note.id)} style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: 14 }}>🗑️</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'rentcast' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h4 style={{ color: theme.text, fontSize: 16, fontWeight: 700, margin: 0 }}>🏠 RentCast Property Data</h4>
                  <p style={{ color: theme.muted, fontSize: 12, margin: '4px 0 0' }}>Get real-time property valuation and comps</p>
                </div>
                <button onClick={fetchRentCast} disabled={loading} style={{ padding: '12px 24px', background: loading ? theme.muted : theme.gradient3, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>{loading ? '⏳ Loading...' : '🔄 Fetch RentCast Data'}</button>
              </div>
              {formData.rentcastData ? (
                <div style={{ display: 'grid', gap: 20 }}>
                  <div style={{ background: theme.success + '15', border: `1px solid ${theme.success}40`, borderRadius: 16, padding: 20 }}>
                    <h5 style={{ color: theme.success, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>💰 Property Valuation</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                      <div><div style={{ color: theme.muted, fontSize: 11 }}>ESTIMATED VALUE</div><div style={{ color: theme.text, fontSize: 24, fontWeight: 800 }}>{fmt(formData.rentcastData.price)}</div></div>
                      <div><div style={{ color: theme.muted, fontSize: 11 }}>PRICE/SQFT</div><div style={{ color: theme.text, fontSize: 24, fontWeight: 800 }}>{fmt(formData.rentcastData.pricePerSquareFoot)}</div></div>
                      <div><div style={{ color: theme.muted, fontSize: 11 }}>LOW ESTIMATE</div><div style={{ color: theme.text, fontSize: 24, fontWeight: 800 }}>{fmt(formData.rentcastData.priceLow)}</div></div>
                      <div><div style={{ color: theme.muted, fontSize: 11 }}>HIGH ESTIMATE</div><div style={{ color: theme.text, fontSize: 24, fontWeight: 800 }}>{fmt(formData.rentcastData.priceHigh)}</div></div>
                    </div>
                  </div>
                  {formData.comps && formData.comps.length > 0 && (
                    <div>
                      <h5 style={{ color: theme.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📊 Comparable Sales ({formData.comps.length})</h5>
                      <div style={{ display: 'grid', gap: 12 }}>
                        {formData.comps.map((comp, i) => (
                          <div key={i} style={{ background: theme.input, borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>{comp.formattedAddress || comp.address}</div>
                              <div style={{ color: theme.muted, fontSize: 12, marginTop: 4 }}>{comp.bedrooms} bed • {comp.bathrooms} bath • {comp.squareFootage?.toLocaleString()} sqft</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ color: theme.success, fontSize: 18, fontWeight: 700 }}>{fmt(comp.price)}</div>
                              <div style={{ color: theme.muted, fontSize: 11 }}>{fmt(comp.pricePerSquareFoot)}/sqft</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 60, color: theme.muted }}>
                  <div style={{ fontSize: 60, marginBottom: 16 }}>🏠</div>
                  <div style={{ fontSize: 16, marginBottom: 8 }}>No RentCast data yet</div>
                  <div style={{ fontSize: 13 }}>Click "Fetch RentCast Data" to get property valuation</div>
                </div>
              )}
            </div>
          )}

          {tab === 'history' && (
            <div>
              <h4 style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📈 Deal History</h4>
              {(formData.history || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: theme.muted }}>No history yet</div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 15, top: 0, bottom: 0, width: 2, background: theme.gradient1 }} />
                  {(formData.history || []).map((h, i) => {
                    const stage = ALL_STAGES.find(s => s.id === h.stage);
                    return (
                      <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 16, position: 'relative' }}>
                        <div style={{ width: 32, height: 32, background: stage?.color || theme.muted, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, zIndex: 1 }}>{stage?.icon || '📍'}</div>
                        <div style={{ flex: 1, background: theme.input, borderRadius: 10, padding: 14 }}>
                          <div style={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>{stage?.label || h.stage}</div>
                          <div style={{ color: theme.muted, fontSize: 12, marginTop: 4 }}>{h.user} • {fmtDate(h.date)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            {deal && role.canDelete && <button onClick={() => { onDelete(deal.id); onClose(); }} style={{ padding: '12px 20px', background: theme.danger + '20', border: `1px solid ${theme.danger}40`, borderRadius: 10, color: theme.danger, fontWeight: 600, cursor: 'pointer' }}>🗑️ Delete</button>}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={onClose} style={{ padding: '12px 24px', background: theme.input, border: `1px solid ${theme.border}`, borderRadius: 10, color: theme.text, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} style={{ padding: '12px 32px', background: theme.gradient1, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>💾 Save Deal</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────────
const App = () => {
  const { theme } = useTheme();
  const [user, setUser] = useStorage('ncc-user', null);
  const [view, setView] = useState('dashboard');
  const [editingDeal, setEditingDeal] = useState(null);
  const [showAddDeal, setShowAddDeal] = useState(false);

  const [users, setUsers] = useStorage('ncc-users', DEFAULT_USERS);
  const [deals, setDeals] = useStorage('ncc-deals', INITIAL_DEALS);
  const [buyers, setBuyers] = useStorage('ncc-buyers', INITIAL_BUYERS);
  const [agents, setAgents] = useStorage('ncc-agents', INITIAL_AGENTS);
  const [contractors, setContractors] = useStorage('ncc-contractors', INITIAL_CONTRACTORS);
  const [flatFeeServices] = useState(INITIAL_FLAT_FEE);
  const [showings, setShowings] = useStorage('ncc-showings', INITIAL_SHOWINGS);

  const handleLogin = (u) => setUser(u);
  const handleLogout = () => { setUser(null); setView('dashboard'); };

  const saveDeal = (deal) => {
    if (deals.find(d => d.id === deal.id)) {
      setDeals(prev => prev.map(d => d.id === deal.id ? deal : d));
    } else {
      setDeals(prev => [...prev, deal]);
    }
    setEditingDeal(null);
    setShowAddDeal(false);
  };

  const deleteDeal = (id) => {
    if (confirm('Are you sure you want to delete this deal?')) {
      setDeals(prev => prev.filter(d => d.id !== id));
    }
  };

  if (!user) {
    return <LoginScreen users={users} onLogin={handleLogin} />;
  }

  const role = ROLES[user.role];

  const renderView = () => {
    switch (view) {
      case 'dashboard':
        return <MegaDashboard deals={deals} buyers={buyers} agents={agents} contractors={contractors} showings={showings} user={user} />;
      case 'deals':
        return <DealPipeline deals={deals} setDeals={setDeals} buyers={buyers} agents={agents} onEditDeal={setEditingDeal} user={user} />;
      case 'showings':
        return <ShowingsTracker deals={deals} showings={showings} setShowings={setShowings} user={user} />;
      case 'tracker':
        return <PerformanceTracker deals={deals} />;
      case 'timeline':
        return <EnhancedTimeline deals={deals} />;
      case 'network':
        return <NetworkView buyers={buyers} setBuyers={setBuyers} agents={agents} setAgents={setAgents} contractors={contractors} setContractors={setContractors} user={user} />;
      case 'markets':
        return <MarketsView deals={deals} buyers={buyers} agents={agents} />;
      case 'flatfee':
        return <FlatFeeView services={flatFeeServices} />;
      case 'financials':
        return <FinancialsView deals={deals} />;
      case 'onhold':
        return <OnHoldView deals={deals} setDeals={setDeals} user={user} />;
      case 'dead':
        return <DeadDealsView deals={deals} setDeals={setDeals} user={user} />;
      case 'admin':
        return role.canManageUsers ? <AdminView users={users} setUsers={setUsers} currentUser={user} /> : <MegaDashboard deals={deals} buyers={buyers} agents={agents} contractors={contractors} showings={showings} user={user} />;
      case 'settings':
        return <SettingsView deals={deals} setDeals={setDeals} buyers={buyers} setBuyers={setBuyers} agents={agents} setAgents={setAgents} contractors={contractors} setContractors={setContractors} users={users} setUsers={setUsers} showings={showings} setShowings={setShowings} user={user} />;
      default:
        return <MegaDashboard deals={deals} buyers={buyers} agents={agents} contractors={contractors} showings={showings} user={user} />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg }}>
      <Sidebar view={view} setView={setView} user={user} onLogout={handleLogout} deals={deals} showings={showings} />
      
      <div style={{ marginLeft: 260 }}>
        <SuperTicker deals={deals} buyers={buyers} agents={agents} showings={showings} />
        
        <div style={{ padding: '0 0 100px' }}>
          {renderView()}
        </div>

        {/* Floating Add Deal Button */}
        {role.canAdd && view === 'deals' && (
          <button
            onClick={() => setShowAddDeal(true)}
            style={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: theme.gradient1,
              border: 'none',
              color: '#fff',
              fontSize: 28,
              cursor: 'pointer',
              boxShadow: theme.glow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s',
              zIndex: 50,
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >+</button>
        )}
      </div>

      {/* Deal Modal */}
      {(editingDeal || showAddDeal) && (
        <DealModal
          deal={editingDeal}
          onClose={() => { setEditingDeal(null); setShowAddDeal(false); }}
          onSave={saveDeal}
          onDelete={deleteDeal}
          buyers={buyers}
          agents={agents}
          user={user}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// EXPORT
// ─────────────────────────────────────────────────────────────────────────────────
export default function NovationCommandCenter() {
  return (
    <ThemeProvider>
      <App />
    </ThemeProvider>
  );
}